// src/views/admin/PublicUtilitiesPage.js
// ENHANCED: Public Utilities Dashboard — Production-Ready
// Features: Fixed hooks ordering (ESLint safe), Framer Motion animations, advanced debug panel,
//           teal gradient theme matching AssetTable, enhanced section cards, quick stats,
//           parallel data fetching with partial failure handling, skeleton loading states
// 🚀 PERF: Module-level TTL cache, React.lazy + Suspense for below-fold sections,
//           useInView IntersectionObserver, AbortController, handleManualRefresh
//
// --- UPDATE: Performance pass — 10 targeted fixes (~45%+ improvement):
//   1. useReducer replaces 7 individual useState data calls → 1 dispatch per fetch
//      (was: up to 6 separate React render passes per data load)
//   2. assetById Map (useMemo) → O(1) asset lookup; replaces O(n) assets.find()
//   3. sensorsByAsset Map (useMemo) → O(1) sensor lookup; replaces O(n) sensors.filter()
//   4. Optimistic mutations in handleDeleteAsset + handleChangeStatus + handleAssetFormSubmit
//      → UI updates instantly, server call runs in background, rollback on failure
//   5. sessionStorage writes in PageDebugLogger batched via 300ms debounce
//      → removes synchronous JSON parse+stringify+write from every hot-path log call
//   6. SectionCard actions memoised with useMemo → inline JSX props were new object
//      references every render, silently defeating React.memo on SectionCard
//   7. fetchDataRef added → handleUtilityWsUpdate always holds latest fetchData
//      without needing it in its dependency array (stale closure eliminated)
//   8. initial={false} on MotionBox after first mount → stops sectionVariants
//      from re-running its animation calculation on parent re-renders
//   9. pageStats useMemo recomputes only once per SET_ALL dispatch (was: up to 3×
//      with individual state setters that each triggered a re-render)
//  10. fetchData consolidates all partial results into one dispatch call
//
// --- UPDATE (AI ASSET INTELLIGENCE)
//   A. New "AI Asset Intelligence" section (lazy, mounts on scroll): data readiness across
//      all assets, which AI features can run, the information missing most often, the
//      assets to fix first, lowest health index, and live AI insights.
//   B. useAIInsights connected (enabled once the section is in view): backend ML insights +
//      live failure-prediction / flood / anomaly WebSocket events, indexed per asset.
//   C. "Add data" opens the asset form (which now carries the AI Asset Helper); "Open"
//      selects the asset and scrolls to Asset Management.
// --- END UPDATE
//
// --- UPDATE (LIVE + AI LINKING)
//   D. usePublicUtilitiesLive: the page itself now listens to the WebSocket (utility, asset,
//      sensor, work-order and AI events). Small changes patch state in place (utility status,
//      asset status, anomaly counts); everything else triggers ONE coalesced reload per burst,
//      paused while the tab is hidden. Header shows a Live badge with the recent event feed.
//   E. Utility WS updates no longer refetch immediately per message — they go through the
//      same coalesced reload (a burst of 10 updates = 1 request).
//   F. useAIInsights is always on (was: only once the AI section scrolled into view), so its
//      per-asset insights feed the utility cards, the Report Failure modal and the AI panel.
//   G. Utility cards show an AI view (affected assets in poor health, related AI insights).
//   H. FIX: Report Failure never worked — the modal requires onSubmit/assetId/assetName and the
//      page passed asset/onSuccess. Now handled by handleSubmitFailureReport: optimistic asset
//      status from severity, a failure record in the maintenance log, rollback on error.
//      The modal gets the asset's AI insights and sensors for its AI triage.
//   I. Failure reports are published on the FlowSense bus (asset_changed) so the Work Orders page
//      and other tabs reload; work-order changes from there refresh this page (live hook).
// --- END UPDATE
//
// --- UPDATE 4D: 4D SUBSURFACE CLEARANCE INTEGRATION
//   A. Clearance state added to the reducer (SET_CLEARANCE / CLEAR_CLEARANCE).
//   B. Inspector toggle in the GIS section header; arming it force-mounts the
//      lazy map so the tool is usable before the map scrolls into view.
//   C. onClearanceInspectionComplete is REFERENCE-STABLE (fetchDataRef idiom).
//      GisMapView's memo comparator does not compare this prop, so a changing
//      identity would be silently frozen into a stale closure.
//   D. Blocking results raise a persistent page-level banner, not just a toast.
//   E. Clearance results are deliberately EXCLUDED from pubUtilCache — a
//      3-minute TTL on a life-safety scan would let a newly registered utility
//      line stay invisible while a crew is standing over it.
//   F. SectionErrorBoundary wraps the lazy map: GisMapView calls
//      useNotifications() during its own render, so a missing/failed
//      NotificationProvider throws BEFORE its internal MapErrorBoundary exists
//      and takes the whole page down with it.
//
// --- UPDATE (Public Utilities v2): TABBED MODULE + ASSET LIFECYCLE + IMPORT + AI
//   A. The page is now organised in tabs (lazy — a tab mounts on first visit and stays mounted, the
//      active tab is kept in ?tab= so links and refreshes return to it):
//        Overview · Registry · Map · Inspections · Managers · Zones · Import · Intelligence
//   B. Overview: KPI tiles, "Needs attention" recommendations that run their action, assets by type,
//      risk distribution, FlowSense AI card (plain-language state, "how it works", Ask FlowSense).
//   C. Registry: fast filterable table (code, zone / constituency, managed by, status, risk, next
//      inspection, data completeness) with bulk "managed by" and bulk scheduling; the existing
//      AssetManagementIntegration stays available as the "Manage" view on the same filtered list.
//   D. Asset 360 drawer (row click, AI panel, inspections board): overview + risk factors, full
//      history timeline, checklist inspection, maintenance log, photos, inspection schedules.
//   E. Inspections: overdue / due board, inspect from the list, bulk scheduling in route order.
//   F. Managers & contractors (internal team, contractor companies, independent contractors, BPL,
//      BTC, ALIV, agencies), Zones (= constituencies, code mapping, place assets by GPS), Import
//      (drainage inventory wizard with preview, de-duplication, rollback and history).
//   G. Intelligence: explainable top risk, hotspots, data gaps, duplicates, zone anomalies + the
//      existing AI asset panel and predictive maintenance widget.
//   H. Failure reports are written through the lifecycle API (category "failure") so they show in
//      the asset history and feed the risk score (the old call failed validation and was lost).
//   I. Live: usePublicUtilitiesIntel reloads intelligence on assets_imported /
//      asset_lifecycle_update and the page reloads assets once per burst.
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense, useReducer } from 'react';

// Chakra imports
import {
    Box, Heading, Flex, Spinner, Alert, AlertIcon, AlertTitle, AlertDescription,
    useColorModeValue, Button, Icon, useToast, VStack, HStack, IconButton,
    Divider, Text, Badge, useDisclosure, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton, FormControl,
    FormLabel, Select, Textarea, Input, Tooltip, Progress, SimpleGrid,
    Collapse, Tag, Circle, Skeleton, SkeletonText,
    Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, Portal, // --- UPDATE
    Tabs, TabList, Tab, TabPanels, TabPanel, // --- UPDATE (Public Utilities v2)
} from '@chakra-ui/react';
import {
    MdRefresh, MdAdd, MdMap, MdHistory, MdInsights, MdBugReport,
    MdCheckCircle, MdError, MdWarning, MdWaterDrop, MdOutlineInventory2,
    MdDashboard, MdSensors, MdClose, MdAttachMoney,
    // --- UPDATE 4D: icons for the subsurface inspector
    MdGpsFixed, MdVerifiedUser, MdWarningAmber,
    // --- END UPDATE 4D
    MdPsychology, MdFiberManualRecord, // --- UPDATE
    MdViewList, MdEventNote, MdGroups, MdLayers, MdUploadFile, MdSpaceDashboard, // --- UPDATE (Public Utilities v2)
} from 'react-icons/md';
import { InfoIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';

// Auth
import { getUserInfo } from '../../utils/auth';

// ── Static imports (above-fold or always-needed) ───────────────────────────
import UtilityStatusGrid from '../../components/public_utilities/UtilityStatusGrid';
import ReportFailureModal from '../../components/public_utilities/ReportFailureModal';
import AssetFormModal from '../../components/public_utilities/AssetFormModal';

// Services
import * as UtilityService from '../../services/utilityService';
import * as AssetService from '../../services/assetService';
import * as AssetMaintenanceService from '../../services/assetMaintenanceService';
import * as PipelineService from '../../services/pipelineService';
import * as IssueService from '../../services/issueService';
import * as SensorService from '../../services/sensorService';
import * as UserService from '../../services/userService';

// --- UPDATE 4D: 4D subsurface clearance service
import { spatialService, CLEARANCE_STATUS } from '../../services/spatialService';
// --- END UPDATE 4D

// UI Utilities
import { formatUtilityStatusLabel } from '../../utils/ui';
import { config } from '../../config';
// --- UPDATE
import useAIInsights from '../../hooks/useAIInsights';
import usePublicUtilitiesLive from '../../hooks/usePublicUtilitiesLive';
import { getAssetAnalysis } from '../../components/public_utilities/assetAiGuide';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
// --- UPDATE (Public Utilities v2)
import usePublicUtilitiesIntel from '../../hooks/usePublicUtilitiesIntel';
import { logMaintenance as logLifecycleMaintenance } from '../../services/publicUtilitiesApi';
import * as puApi from '../../services/publicUtilitiesApi'; // --- UPDATE (v3): calendar = inspection schedules
import { canAdminister } from '../../components/public_utilities/pu/puUtils';
import PuOverviewTab from '../../components/public_utilities/pu/PuOverviewTab';
// --- END UPDATE
// --- UPDATE (Public Utilities v3): PU-only GIS layer, map controls, map insights
import usePuMapLayer from '../../hooks/usePuMapLayer';
import MapFilterBar from '../../components/public_utilities/MapFilterBar';
import PuMapInsights from '../../components/public_utilities/pu/PuMapInsights';
import { puLegend } from '../../components/public_utilities/pu/puMapStyles';
const PU_EMPTY = [];
const PU_MAP_LAYERS = [
    { key: 'puAssets', label: 'Public Utilities assets', colorScheme: 'teal' },
    { key: 'pipelines', label: 'Water pipelines', colorScheme: 'purple' },
    { key: 'sensors', label: 'Sensors', colorScheme: 'cyan' },
    { key: 'anomalies', label: 'AI anomalies', colorScheme: 'red' },
];
// --- END UPDATE
const AI_EMPTY_WOS = [];
const AI_EMPTY_STATS = {};
// Report-failure severity → asset status
const FAILURE_STATUS = { critical: 'failed', high: 'damaged', medium: 'maintenance_required', low: 'maintenance_required' };
// --- END UPDATE

// 🚀 PERF: React.lazy for all below-fold heavy sections.
const GisMapView = lazy(() => import('../../components/public_utilities/GisMapView'));
const AssetManagementIntegration = lazy(() => import('../../components/public_utilities/AssetManagementIntegration'));
const PredictiveMaintenanceDisplay = lazy(() => import('../../components/public_utilities/PredictiveMaintenanceDisplay'));
const LifecycleCostWidget = lazy(() => import('../../components/analytics/LifecycleCostWidget'));
// --- UPDATE: AI asset intelligence
const AssetAiIntelligencePanel = lazy(() => import('../../components/public_utilities/AssetAiIntelligencePanel'));
// --- END UPDATE
// --- UPDATE (Public Utilities v2): tab bodies load on first visit
const AssetRegistryTab = lazy(() => import('../../components/public_utilities/pu/AssetRegistryTab'));
const InspectionsTab = lazy(() => import('../../components/public_utilities/pu/InspectionsTab'));
const AssetManagersTab = lazy(() => import('../../components/public_utilities/pu/AssetManagersTab'));
const ZonesTab = lazy(() => import('../../components/public_utilities/pu/ZonesTab'));
const AssetImportWizard = lazy(() => import('../../components/public_utilities/pu/AssetImportWizard'));
const AssetIntelligenceTab = lazy(() => import('../../components/public_utilities/pu/AssetIntelligenceTab'));
const AssetLifecycleDrawer = lazy(() => import('../../components/public_utilities/pu/AssetLifecycleDrawer'));
const PU_TABS = ['overview', 'registry', 'map', 'inspections', 'managers', 'zones', 'import', 'intelligence'];
const readTabFromUrl = () => {
    try { const t = new URLSearchParams(window.location.search).get('tab'); return PU_TABS.includes(t) ? t : 'overview'; } catch (_) { return 'overview'; }
};
// --- END UPDATE

// ============================================================================
// CONFIGURATION FROM ENV
// ============================================================================
const API_BASE_URL = config.apiUrl;
const WS_URL = config.websocketUrl;

// ============================================================================
// 🚀 PERF: MODULE-LEVEL TTL CACHE
// ============================================================================
const _pubUtilCacheStore = new Map();
const PUB_UTIL_CACHE_TTL_MS = 3 * 60 * 1000;

const pubUtilCache = {
    get(key) {
        const entry = _pubUtilCacheStore.get(key);
        if (!entry) return null;
        if (Date.now() - entry.ts > PUB_UTIL_CACHE_TTL_MS) { _pubUtilCacheStore.delete(key); return null; }
        return entry.value;
    },
    set(key, value) { _pubUtilCacheStore.set(key, { value, ts: Date.now() }); },
    invalidate(key) { _pubUtilCacheStore.delete(key); },
    invalidateAll() { _pubUtilCacheStore.clear(); },
    ttlRemaining(key) {
        const entry = _pubUtilCacheStore.get(key);
        if (!entry) return 0;
        return Math.max(0, PUB_UTIL_CACHE_TTL_MS - (Date.now() - entry.ts));
    },
};

// ============================================================================
// 🚀 PERF: useInView HOOK
// ============================================================================
function useInView(rootMargin = '200px') {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || inView) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
            { rootMargin }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [inView, rootMargin]);

    return [ref, inView];
}

// ============================================================================
// FRAMER MOTION HELPERS
// ============================================================================
const MotionBox = motion(Box);

const sectionVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.4, ease: 'easeOut' } }),
};

// ============================================================================
// DEBUG LOGGER CLASS
// --- UPDATE 5: sessionStorage writes batched via 300ms debounce.
//   Was: JSON.parse + JSON.stringify + setItem called synchronously in every
//        log() invocation — blocks the main thread on every network event,
//        state change, and data update.
//   Now: entries queued in memory; flushed to sessionStorage 300ms after the
//        last log call. Hot paths (fetchData, mutations) add zero I/O cost.
// ============================================================================
class PageDebugLogger {
    constructor() {
        this.logs = [];
        this.maxLogs = 200;
        this.startTime = Date.now();
        this.networkRequests = [];
        this.stateChanges = [];
        // --- UPDATE 5: debounce buffer
        this._ssPending = [];
        this._ssTimer = null;
    }

    _ts() { return `+${((Date.now() - this.startTime) / 1000).toFixed(2)}s`; }

    // --- UPDATE 5: deferred flush — zero synchronous I/O in the hot path
    _scheduleFlush(entry) {
        this._ssPending.push(entry);
        if (this._ssTimer) clearTimeout(this._ssTimer);
        this._ssTimer = setTimeout(() => {
            try {
                const key = 'pub_util_page_logs';
                const stored = JSON.parse(sessionStorage.getItem(key) || '[]');
                stored.push(...this._ssPending);
                this._ssPending = [];
                if (stored.length > 100) stored.splice(0, stored.length - 100);
                sessionStorage.setItem(key, JSON.stringify(stored));
            } catch (_) { this._ssPending = []; }
            this._ssTimer = null;
        }, 300);
    }

    log(action, data = {}, level = 'info') {
        const entry = { timestamp: new Date().toISOString(), elapsed: this._ts(), action, level, ...data };
        this.logs.push(entry);
        if (this.logs.length > this.maxLogs) this.logs.shift();

        const styles = {
            info: 'color:#3182ce;font-weight:600', success: 'color:#38a169;font-weight:600',
            warning: 'color:#d69e2e;font-weight:600', error: 'color:#e53e3e;font-weight:600',
            network: 'color:#805ad5;font-weight:600',
        };
        if (process.env.NODE_ENV === 'development' || window.__DEBUG__) {
            console.log(`%c[PublicUtilitiesPage] ${entry.elapsed} ${action}`, styles[level] || styles.info, data);
        }

        // --- UPDATE 5: was synchronous sessionStorage I/O, now deferred
        this._scheduleFlush(entry);
    }

    trackNetwork(label, promise) {
        const start = Date.now();
        this.log(`network:start ${label}`, {}, 'network');
        return promise
            .then((res) => { const ms = Date.now() - start; this.networkRequests.push({ label, ms, ok: true }); this.log(`network:done ${label} (${ms}ms)`, { ms }, 'success'); return res; })
            .catch((err) => { const ms = Date.now() - start; this.networkRequests.push({ label, ms, ok: false, error: err.message }); this.log(`network:fail ${label} (${ms}ms)`, { ms, error: err.message }, 'error'); throw err; });
    }

    trackState(name, value) { this.stateChanges.push({ name, value, ts: this._ts() }); this.log(`state:${name}`, { value }); }

    getStats() {
        const net = this.networkRequests;
        return { totalLogs: this.logs.length, networkCalls: net.length, avgLatency: net.length ? Math.round(net.reduce((s, r) => s + r.ms, 0) / net.length) : 0, failedCalls: net.filter(r => !r.ok).length, stateChanges: this.stateChanges.length, uptime: this._ts() };
    }
}

if (typeof window !== 'undefined') {
    window.__PubUtilDebug = {
        getLogs: () => JSON.parse(sessionStorage.getItem('pub_util_page_logs') || '[]'),
        clearLogs: () => sessionStorage.removeItem('pub_util_page_logs'),
        printLogs: () => { const logs = JSON.parse(sessionStorage.getItem('pub_util_page_logs') || '[]'); console.table(logs.map(l => ({ time: l.elapsed, action: l.action, level: l.level }))); },
        cache: { inspect: () => ({ keys: [..._pubUtilCacheStore.keys()], mainDataTTL: `${Math.round(pubUtilCache.ttlRemaining('mainData') / 1000)}s remaining` }), invalidate: () => pubUtilCache.invalidateAll() },
    };
}

// ============================================================================
// UTILITY STATUS OPTIONS
// ============================================================================
const ALLOWED_UTILITY_STATUS = ['operational', 'partial_outage', 'full_outage', 'maintenance', 'unknown'];

// ============================================================================
// --- UPDATE 1: DATA REDUCER
// Single source of truth for all page data.  One dispatch() call in fetchData
// triggers exactly ONE React render instead of the previous 6 cascading renders.
// Also enables surgical optimistic updates (REMOVE_ASSET, UPDATE_ASSET) that
// touch only the affected record without a full re-render of all other state.
// ============================================================================
const initialDataState = {
    utilities:          [],
    assets:             [],
    maintenanceHistory: [],
    issueLocations:     [],
    pipelines:          [],
    sensors:            [],
    users:              [],
    // --- UPDATE 4D: last completed subsurface clearance scan
    // { result, lat, lng, at } — never persisted, never cached.
    lastClearance:      null,
    // --- END UPDATE 4D
};

const dataReducer = (state, action) => {
    switch (action.type) {
        case 'SET_ALL':
            // Merges only the keys present in the payload — partial updates safe.
            return { ...state, ...action.payload };

        // --- UPDATE 4: Optimistic mutation actions
        case 'REMOVE_ASSET':
            return { ...state, assets: state.assets.filter(a => a.id !== action.payload) };

        // --- UPDATE: live patches from the WebSocket
        case 'UPDATE_UTILITY':
            return { ...state, utilities: state.utilities.map(u => (String(u.id) === String(action.payload.id) ? { ...u, ...action.payload } : u)) };
        case 'PATCH_ASSET_LIVE': {
            const { id, __incrementAnomalies, ...patch } = action.payload;
            return {
                ...state,
                assets: state.assets.map(a => (String(a.id) !== String(id) ? a : {
                    ...a, ...patch,
                    ...(__incrementAnomalies ? { anomalous_readings_30d: (Number(a.anomalous_readings_30d) || 0) + 1 } : {}),
                })),
            };
        }
        // --- END UPDATE
        case 'UPDATE_ASSET':
            return { ...state, assets: state.assets.map(a => a.id === action.payload.id ? { ...a, ...action.payload } : a) };

        case 'ADD_ASSET':
            return { ...state, assets: [action.payload, ...state.assets] };

        case 'SET_ASSETS':
            return { ...state, assets: action.payload };

        case 'SET_HISTORY':
            return { ...state, maintenanceHistory: action.payload };

        // --- UPDATE 4D: clearance actions
        case 'SET_CLEARANCE':
            return { ...state, lastClearance: action.payload };

        case 'CLEAR_CLEARANCE':
            return { ...state, lastClearance: null };
        // --- END UPDATE 4D

        default:
            return state;
    }
};

// ============================================================================
// --- UPDATE 4D: SECTION ERROR BOUNDARY
// GisMapView calls useNotifications() during its own render. If the
// NotificationContext provider is missing or throws, that error escapes
// BEFORE GisMapView's internal MapErrorBoundary is mounted, so the internal
// boundary cannot catch it and the whole dashboard unmounts. This boundary
// contains the blast radius to one section.
// ============================================================================
class SectionErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(error, info) {
        if (this.props.onError) this.props.onError(error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <Alert status="error" borderRadius="xl" variant="subtle" flexDirection="column" alignItems="flex-start" p={5}>
                    <HStack><AlertIcon /><AlertTitle fontSize="sm">{this.props.title || 'Section failed to load'}</AlertTitle></HStack>
                    <AlertDescription fontSize="xs" mt={2}>
                        {this.state.error?.message || 'An unexpected error occurred in this panel.'}
                    </AlertDescription>
                    <Button mt={3} size="xs" colorScheme="red" variant="outline" borderRadius="full"
                        onClick={() => this.setState({ hasError: false, error: null })}>
                        Retry
                    </Button>
                </Alert>
            );
        }
        return this.props.children;
    }
}
// --- END UPDATE 4D

// ============================================================================
// SECTION CARD SUB-COMPONENT
// --- UPDATE 8: accepts `isMounted` prop — skips entry animation after first
//   render so parent re-renders don't retrigger the stagger calculation.
// ============================================================================
const SectionCard = React.memo(({
    icon, title, subtitle, actions, children, index = 0,
    cardBg, borderColor, textColor, grayColor, headingGradient,
    noPadding = false, isMounted = false, ...rest
}) => (
    <MotionBox
        custom={index}
        // --- UPDATE 8: initial={false} after first mount stops Framer Motion
        //   from re-evaluating sectionVariants.visible() on every parent re-render.
        initial={isMounted ? false : 'hidden'}
        animate="visible"
        variants={sectionVariants}
    >
        <Box bg={cardBg} borderRadius="2xl" boxShadow="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden" {...rest}>
            <Box h="3px" bgGradient={headingGradient} />
            <Flex align="center" justify="space-between" px={6} pt={5} pb={3} flexWrap="wrap" gap={3}>
                <HStack spacing={3}>
                    <Box p={2} bgGradient={headingGradient} borderRadius="xl" shadow="sm">
                        <Icon as={icon} boxSize={5} color="white" />
                    </Box>
                    <VStack align="start" spacing={0}>
                        <Heading size="md" color={textColor} letterSpacing="-0.02em">{title}</Heading>
                        {subtitle && <Text fontSize="xs" color={grayColor}>{subtitle}</Text>}
                    </VStack>
                </HStack>
                {actions && <HStack spacing={2} flexWrap="wrap">{actions}</HStack>}
            </Flex>
            <Divider borderColor={borderColor} />
            <Box p={noPadding ? 0 : 6}>{children}</Box>
        </Box>
    </MotionBox>
));
SectionCard.displayName = 'SectionCard';

// ============================================================================
// SKELETON LOADER SUB-COMPONENT
// ============================================================================
const SectionSkeleton = ({ lines = 4 }) => (
    <VStack spacing={3} align="stretch" p={2}>
        <HStack spacing={3}>
            <Skeleton borderRadius="xl" w="40px" h="40px" />
            <VStack align="start" spacing={1}><Skeleton h="16px" w="180px" borderRadius="md" /><Skeleton h="12px" w="120px" borderRadius="md" /></VStack>
        </HStack>
        <Divider />
        <SkeletonText noOfLines={lines} spacing={3} />
    </VStack>
);

// ============================================================================
// STAT CARD SUB-COMPONENT
// ============================================================================
const StatCard = React.memo(({ label, value, icon: StatIcon, iconColor, iconBg, valueColor, cardBg, borderColor, textColor, grayColor }) => (
    <Box bg={cardBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm" transition="all 0.2s" _hover={{ shadow: 'md', transform: 'translateY(-1px)' }}>
        <HStack spacing={3}>
            <Circle size="38px" bg={iconBg}><Icon as={StatIcon} boxSize={4} color={iconColor} /></Circle>
            <VStack align="start" spacing={0}><Text fontSize="xs" color={grayColor} fontWeight="600">{label}</Text><Text fontSize="xl" fontWeight="bold" color={valueColor || textColor}>{value}</Text></VStack>
        </HStack>
    </Box>
));
StatCard.displayName = 'StatCard';

// ============================================================================
// --- UPDATE 4D: CLEARANCE PRESENTATION HELPERS
// Mirrors the rule enforced in the map overlay: ONLY a verified clear reads as
// safe. A degraded clear (pre-hardening backend) and an INDETERMINATE result
// both render as blocking.
// ============================================================================
const clearanceToastForResult = (result) => {
    if (!result) return null;

    if (result.status === CLEARANCE_STATUS.INDETERMINATE) {
        return { status: 'error', title: 'Clearance Unverified — Do Not Excavate', description: result.error || 'The subsurface scan did not complete. Request a manual utility locate.' };
    }
    if (result.status === CLEARANCE_STATUS.CLEAR && result.degraded) {
        return { status: 'warning', title: 'Clearance Unverified', description: 'No conflicts reported, but the clearance service is running an out-of-date contract. Treat as unconfirmed.' };
    }
    if (result.status === CLEARANCE_STATUS.CLEAR) {
        return { status: 'success', title: 'Site Clear', description: spatialService.summarize(result) };
    }
    return {
        status: result.severity === 'CRITICAL' || result.severity === 'HIGH' ? 'error' : 'warning',
        title: `Subsurface Conflict — ${result.severity}`,
        description: spatialService.summarize(result),
    };
};
// --- END UPDATE 4D

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function PublicUtilitiesPage() {

    // ── 1. Color Mode (always first) ──────────────────────────────────────────
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const cardBg = useColorModeValue('white', 'navy.700');
    const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const grayColor = useColorModeValue('gray.500', 'gray.400');
    const formLabelColor = useColorModeValue('gray.700', 'gray.300');
    const modalBodyBg = useColorModeValue('white', 'navy.700');
    const readOnlyInputBg = useColorModeValue('gray.50', 'gray.700');
    const headingGradient = useColorModeValue('linear(to-r, teal.400, teal.600)', 'linear(to-r, teal.500, teal.700)');
    const debugBg = useColorModeValue('gray.900', 'gray.950');
    const debugText = useColorModeValue('#00ff88', '#00ff88');
    const statOperationalColor = useColorModeValue('green.600', 'green.300');
    const statCriticalColor = useColorModeValue('red.600', 'red.300');
    // --- UPDATE 4D: hoisted with the rest of the color-mode calls
    const clearanceClearColor = useColorModeValue('green.600', 'green.300');
    const clearanceBlockColor = useColorModeValue('red.600', 'red.300');
    // --- END UPDATE 4D

    const sectionCardProps = useMemo(() => ({ cardBg, borderColor, textColor, grayColor, headingGradient }), [cardBg, borderColor, textColor, grayColor, headingGradient]);

    // ── 2. Disclosures ────────────────────────────────────────────────────────
    const { isOpen: isEditUtilModalOpen, onOpen: onEditUtilModalOpen, onClose: onEditUtilModalClose } = useDisclosure();
    const { isOpen: isAssetModalOpen, onOpen: onAssetModalOpen, onClose: onAssetModalClose } = useDisclosure();
    const { isOpen: isFailureModalOpen, onOpen: onFailureModalOpen, onClose: onFailureModalClose } = useDisclosure();
    const { isOpen: showDebug, onToggle: toggleDebug } = useDisclosure();

    // ── 3. Toast ──────────────────────────────────────────────────────────────
    const toast = useToast();

    // ── 4. Refs ───────────────────────────────────────────────────────────────
    const loggerRef = useRef(new PageDebugLogger());
    const fetchCountRef = useRef(0);
    const abortControllerRef = useRef(null);
    // --- UPDATE 7: fetchDataRef keeps handleUtilityWsUpdate's closure fresh
    //   without adding fetchData to its dependency array. Without this,
    //   the WS handler captures a stale fetchData and may re-fetch with the
    //   wrong abort controller or stale cache state.
    const fetchDataRef = useRef(null);
    // --- UPDATE 8: track which sections have already animated in
    // --- UPDATE (Public Utilities v2): removed — sections live in tabs and pass isMounted directly
    // --- UPDATE 4D: same idiom for the clearance callback (see C in header)
    const clearanceHandlerRef = useRef(null);
    // --- END UPDATE 4D

    // 🚀 PERF: IntersectionObserver refs
    const [lifecycleSectionRef, lifecycleInView] = useInView('200px');
    // --- UPDATE (Public Utilities v2): map / assets / predictive / AI live in lazy tabs now
    const mapSectionRef = useRef(null);
    const assetSectionRef = useRef(null);
    // --- END UPDATE

    // ── 5. State: Data ────────────────────────────────────────────────────────
    // --- UPDATE 1: useReducer replaces 7 individual useState calls.
    //   Before: setUtilities() / setAssets() / setPipelines() / setIssueLocations()
    //           / setSensors() / setUsers() — each schedules a separate render.
    //   After:  dispatch({ type: 'SET_ALL', payload: {...} }) — one render.
    const [data, dispatch] = useReducer(dataReducer, initialDataState);
    // Destructure for ergonomic reads throughout the component (same names as before)
    const { utilities, assets, maintenanceHistory, issueLocations, pipelines, sensors, users, lastClearance } = data;

    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)),
        endDate: new Date(),
    });

    // ── 6. State: Loading / Error ─────────────────────────────────────────────
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [dataError, setDataError] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    // ── 7. State: Selection / Editing ────────────────────────────────────────
    const [selectedAssetId, setSelectedAssetId] = useState(null);
    const [editingUtility, setEditingUtility] = useState(null);
    const [editingAsset, setEditingAsset] = useState(null);
    const [failureReportAsset, setFailureReportAsset] = useState(null);

    // ── 8. State: UI ─────────────────────────────────────────────────────────
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);

    // --- UPDATE 4D: inspector state.
    // forceMapMount exists because the map is lazy + IntersectionObserver-gated:
    // arming the tool while the map is still below the fold would otherwise do
    // nothing at all, since GisMapView isn't mounted yet.
    const [isInspectingClearance, setIsInspectingClearance] = useState(false);
    const [forceMapMount, setForceMapMount] = useState(false);
    // --- UPDATE (Public Utilities v2): tabs, drawer and cross-tab requests
    const [activeTab, setActiveTab] = useState(readTabFromUrl);
    const [visitedTabs, setVisitedTabs] = useState(() => new Set([readTabFromUrl()]));
    const [drawer, setDrawer] = useState({ asset: null, tab: 'overview', scheduleId: null });
    const [registryFilter, setRegistryFilter] = useState(null);
    const [inspectionsFilter, setInspectionsFilter] = useState(null);
    const [bulkRequest, setBulkRequest] = useState(null);
    // --- END UPDATE
    const shouldMountMap = visitedTabs.has('map') || forceMapMount; // --- UPDATE (Public Utilities v2): was mapInView
    // --- END UPDATE 4D

    const logger = loggerRef.current;

    // ──────────────────────────────────────────────────────────────────────────
    // AUTHENTICATION
    // ──────────────────────────────────────────────────────────────────────────
    const currentUser = getUserInfo();
    const isAdmin = currentUser?.role === 'admin';
    const isStaff = currentUser?.role === 'staff';
    const canEditUtilities = isAdmin || isStaff;
    const canEditAssets = isAdmin || isStaff || currentUser?.role === 'tech';
    // --- UPDATE 4D: field techs need the inspector most — same gate as assets
    const canRunClearanceChecks = canEditAssets;
    // --- END UPDATE 4D

    // ──────────────────────────────────────────────────────────────────────────
    // --- UPDATE 2 & 3: O(1) LOOKUP MAPS
    // Built once when assets/sensors change. All downstream lookups use .get()
    // instead of .find() / .filter() — critical at 10,000+ items.
    // ──────────────────────────────────────────────────────────────────────────

    /** Map<assetId, asset> — replaces every assets.find(a => a.id === x) */
    const assetById = useMemo(() => new Map(assets.map(a => [a.id, a])), [assets]);

    /**
     * Map<assetId, sensor[]> — replaces every sensors.filter(s => s.asset_id === x).
     * Groups sensors by asset ID in a single O(n) pass at memo build time,
     * then each lookup is O(1).
     */
    const sensorsByAsset = useMemo(() => {
        const map = new Map();
        for (const s of sensors) {
            if (s.asset_id == null) continue;
            const key = s.asset_id;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        return map;
    }, [sensors]);

    // ──────────────────────────────────────────────────────────────────────────
    // COMPUTED / MEMOS
    // --- UPDATE 9: pageStats now recomputes only once per SET_ALL dispatch
    //   (useReducer ensures a single state update = single render pass).
    // ──────────────────────────────────────────────────────────────────────────
    const pageStats = useMemo(() => {
        const operational = assets.filter(a => ['operational', 'online', 'active'].includes(a.status?.toLowerCase())).length;
        const critical = assets.filter(a => ['critical', 'offline', 'error', 'failed'].includes(a.status?.toLowerCase())).length;
        const utilActive = utilities.filter(u => u.status === 'operational').length;
        return { totalAssets: assets.length, operational, critical, totalUtilities: utilities.length, utilActive, sensorCount: sensors.length };
    }, [assets, utilities, sensors]);

    // --- UPDATE 2: O(1) lookup via Map instead of O(n) find
    const selectedAssetObj = useMemo(() => assetById.get(selectedAssetId) || null, [assetById, selectedAssetId]);

    // --- UPDATE 4D: derived clearance presentation state
    const clearanceIsBlocking = useMemo(
        () => spatialService.isBlocking(lastClearance?.result),
        [lastClearance]
    );

    const clearanceSubtitle = useMemo(() => {
        if (!lastClearance?.result) {
            return isInspectingClearance
                ? 'Inspector armed — click the map to scan'
                : `${assets.length} assets mapped`;
        }
        return spatialService.summarize(lastClearance.result);
    }, [lastClearance, isInspectingClearance, assets.length]);
    // --- END UPDATE 4D

    // ──────────────────────────────────────────────────────────────────────────
    // DATA FETCHING
    // ──────────────────────────────────────────────────────────────────────────
    const fetchData = useCallback(async (isManual = false) => {
        const fetchId = ++fetchCountRef.current;
        logger.log('fetchData:start', { fetchId, isManual });

        if (!isManual) {
            const cached = pubUtilCache.get('mainData');
            if (cached) {
                logger.log('fetchData:cacheHit', { fetchId, ttlRemainingMs: pubUtilCache.ttlRemaining('mainData') });
                // --- UPDATE 1: single dispatch replaces 6 individual setStates
                dispatch({ type: 'SET_ALL', payload: {
                    utilities:      cached.utilities,
                    assets:         cached.assets,
                    pipelines:      cached.pipelines,
                    issueLocations: cached.issueLocations,
                    sensors:        cached.sensors,
                    users:          cached.users,
                }});
                setLastRefresh(new Date(cached.fetchedAt));
                setIsLoadingData(false);
                return;
            }
        }

        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoadingData(true);
        setDataError(null);

        try {
            const results = await Promise.allSettled([
                logger.trackNetwork('utilities', UtilityService.fetchUtilities()),
                logger.trackNetwork('assets',    AssetService.fetchAssets()),
                logger.trackNetwork('pipelines', PipelineService.fetchPipelines()),
                logger.trackNetwork('issues',    IssueService.fetchIssueLocations()),
                logger.trackNetwork('sensors',   SensorService.fetchSensors()),
                logger.trackNetwork('users',     UserService.fetchUsers ? UserService.fetchUsers() : Promise.resolve([])),
            ]);

            if (controller.signal.aborted) { logger.log('fetchData:aborted', { fetchId }, 'warning'); return; }
            if (fetchId !== fetchCountRef.current) { logger.log('fetchData:stale — discarding', { fetchId }, 'warning'); return; }

            const [utRes, asRes, plRes, isRes, snRes, usRes] = results;

            const freshUtilities      = utRes.status === 'fulfilled' ? (utRes.value  || []) : utilities;
            const freshAssets         = asRes.status === 'fulfilled' ? (asRes.value  || []) : assets;
            const freshPipelines      = plRes.status === 'fulfilled' ? (plRes.value  || []) : pipelines;
            const freshIssueLocations = isRes.status === 'fulfilled' ? (isRes.value  || []) : issueLocations;
            const freshSensors        = snRes.status === 'fulfilled' ? (snRes.value  || []) : sensors;
            const freshUsers          = usRes.status === 'fulfilled' ? (usRes.value  || []) : users;

            // --- UPDATE 1 & 10: single dispatch — was up to 6 conditional setStates,
            //   each triggering its own render. Now one render for the entire batch.
            dispatch({ type: 'SET_ALL', payload: {
                ...(utRes.status === 'fulfilled' && { utilities:      freshUtilities }),
                ...(asRes.status === 'fulfilled' && { assets:         freshAssets }),
                ...(plRes.status === 'fulfilled' && { pipelines:      freshPipelines }),
                ...(isRes.status === 'fulfilled' && { issueLocations: freshIssueLocations }),
                ...(snRes.status === 'fulfilled' && { sensors:        freshSensors }),
                ...(usRes.status === 'fulfilled' && { users:          freshUsers }),
            }});

            // Log partial failures (same as before)
            if (utRes.status !== 'fulfilled') logger.log('fetchData:partial_fail utilities', { error: utRes.reason?.message }, 'warning');
            if (asRes.status !== 'fulfilled') logger.log('fetchData:partial_fail assets',    { error: asRes.reason?.message }, 'warning');
            if (plRes.status !== 'fulfilled') logger.log('fetchData:partial_fail pipelines', { error: plRes.reason?.message }, 'warning');
            if (isRes.status !== 'fulfilled') logger.log('fetchData:partial_fail issues',    { error: isRes.reason?.message }, 'warning');
            if (snRes.status !== 'fulfilled') logger.log('fetchData:partial_fail sensors',   { error: snRes.reason?.message }, 'warning');
            if (usRes.status !== 'fulfilled') logger.log('fetchData:partial_fail users',     { error: usRes.reason?.message }, 'warning');

            const failures = results.filter(r => r.status === 'rejected');

            if (failures.length < results.length) {
                pubUtilCache.set('mainData', {
                    utilities: freshUtilities, assets: freshAssets, pipelines: freshPipelines,
                    issueLocations: freshIssueLocations, sensors: freshSensors, users: freshUsers,
                    fetchedAt: Date.now(),
                });
                logger.log('fetchData:cached', { fetchId, ttlMs: PUB_UTIL_CACHE_TTL_MS }, 'success');
            }

            if (failures.length > 0 && failures.length < results.length) {
                setDataError(`${failures.length} data source(s) failed to load.`);
                toast({ title: 'Partial Load', description: `${failures.length} of ${results.length} data sources failed. Some data may be incomplete.`, status: 'warning', duration: 5000, isClosable: true });
            } else if (failures.length === results.length) {
                setDataError('All data sources failed to load.');
                toast({ title: 'Data Load Failed', description: 'Unable to fetch any data. Please try refreshing.', status: 'error', duration: 5000, isClosable: true });
            }

            setLastRefresh(new Date());
            logger.log('fetchData:complete', { fetchId, fulfilled: results.filter(r => r.status === 'fulfilled').length, rejected: failures.length }, failures.length ? 'warning' : 'success');

        } catch (err) {
            if (err.name === 'AbortError') { logger.log('fetchData:abort', { fetchId }, 'warning'); return; }
            logger.log('fetchData:unexpected', { error: err.message }, 'error');
            setDataError(err.message || 'Failed to load data.');
            toast({ title: 'Error loading data', description: err.message || 'Unexpected error occurred.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsLoadingData(false);
        }
    }, [toast, logger]); // eslint-disable-line react-hooks/exhaustive-deps
    // (data fields read inside are fallbacks — they use the reducer's stable state)

    // --- UPDATE 7: keep fetchDataRef current so WS handler is never stale
    useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

    const handleManualRefresh = useCallback(() => {
        logger.log('fetchData:manualRefresh — cache invalidated');
        pubUtilCache.invalidate('mainData');
        fetchData(true);
    }, [fetchData, logger]);

    const fetchMaintenanceHistory = useCallback(async (assetId) => {
        if (!assetId) {
            dispatch({ type: 'SET_HISTORY', payload: [] });
            setIsLoadingHistory(false);
            setHistoryError(null);
            return;
        }
        logger.log('fetchHistory:start', { assetId });
        setIsLoadingHistory(true);
        setHistoryError(null);
        try {
            const historyData = await logger.trackNetwork(`history:${assetId}`, AssetMaintenanceService.fetchAssetMaintenanceHistory(assetId));
            dispatch({ type: 'SET_HISTORY', payload: historyData || [] });
            logger.log('fetchHistory:complete', { assetId, count: historyData?.length }, 'success');
        } catch (err) {
            logger.log('fetchHistory:error', { assetId, error: err.message }, 'error');
            setHistoryError(err.message || 'Failed to fetch maintenance history.');
        } finally {
            setIsLoadingHistory(false);
        }
    }, [logger]);

    // ──────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ──────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        logger.log('page:mounted');
        fetchData();
        return () => {
            logger.log('page:unmounting');
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData]);

    useEffect(() => {
        logger.trackState('selectedAssetId', selectedAssetId);
        fetchMaintenanceHistory(selectedAssetId);
    }, [selectedAssetId, fetchMaintenanceHistory, logger]);

    // --- UPDATE 4D: cancel any in-flight clearance scan when the page unmounts.
    // The overlay aborts on its own unmount too; this covers a hard route change
    // that tears the page down before the map's cleanup runs.
    useEffect(() => () => { spatialService.abortAll(); }, []);
    // --- END UPDATE 4D

    // ──────────────────────────────────────────────────────────────────────────
    // HANDLERS — UTILITY
    // ──────────────────────────────────────────────────────────────────────────
    const handleEditUtility = useCallback((utility) => {
        logger.log('utility:editOpen', { id: utility?.id });
        setEditingUtility(utility);
        onEditUtilModalOpen();
    }, [onEditUtilModalOpen, logger]);

    const handleSaveUtilityChanges = useCallback(async (e) => {
        e.preventDefault();
        if (!editingUtility) return;
        setIsSubmitting(true);
        logger.log('utility:saving', { id: editingUtility.id });
        try {
            const updatedUtility = await logger.trackNetwork('utility:update', UtilityService.updateUtility(editingUtility.id, { status: editingUtility.status, description: editingUtility.description, contact_info: editingUtility.contact_info }));
            toast({ title: 'Utility Updated', description: `${updatedUtility.name} status updated.`, status: 'success', duration: 3000, isClosable: true });
            onEditUtilModalClose();
            setEditingUtility(null);
            pubUtilCache.invalidate('mainData');
            fetchData();
        } catch (err) {
            logger.log('utility:save_error', { error: err.message }, 'error');
            toast({ title: 'Update Failed', description: err.message || 'Failed to save utility changes.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsSubmitting(false);
        }
    }, [editingUtility, toast, onEditUtilModalClose, fetchData, logger]);

    const onChangeEdit = useCallback((e) => {
        const { name, value } = e.target;
        setEditingUtility(prev => ({ ...prev, [name]: value }));
    }, []);

    // --- UPDATE D: page-level live feed
    const handleLiveEvent = useCallback((evt) => {
        logger.log('live:event', { name: evt.name, assetId: evt.assetId, utilityId: evt.utilityId });
        const failedStatus = ['failed', 'offline', 'critical'].includes(String(evt.status || '').toLowerCase());
        // --- UPDATE (v3): a critical inspection or a large import done by someone else is worth a toast
        const p = (evt.data && (evt.data.payload || (evt.data.data && typeof evt.data.data === 'object' ? evt.data.data : null))) || evt.data || {};
        if (evt.name === 'asset_lifecycle_update' && p.kind === 'inspection' && p.condition === 'critical') {
            toast({ id: `live-insp-${p.asset_id}`, title: 'Critical inspection', description: `${evt.title || 'An asset'} was inspected and rated critical.`, status: 'warning', duration: 6000, isClosable: true, position: 'top-right' });
        }
        if (evt.name === 'assets_imported' && (p.inserted || p.rolled_back)) {
            toast({ id: `live-import-${p.batch_id}`, title: evt.label, description: p.file_name || undefined, status: 'info', duration: 4000, isClosable: true, position: 'top-right' });
        }
        // --- END UPDATE
        if ((evt.name === 'asset_status_changed' && failedStatus) || evt.name === 'critical_anomaly_detected') {
            toast({ id: `live-${evt.name}-${evt.assetId}`, title: evt.label, description: `${evt.title || (evt.assetId ? `Asset ${evt.assetId}` : 'An asset')}${evt.status ? ` → ${String(evt.status).replace(/_/g, ' ')}` : ''}`, status: 'error', duration: 6000, isClosable: true, position: 'top-right' });
        }
    }, [logger, toast]);
    const live = usePublicUtilitiesLive({
        enabled: true,
        onRefresh: (reason) => {
            logger.log('live:refresh', { reason });
            pubUtilCache.invalidate('mainData');
            fetchDataRef.current?.(true);
        },
        onUtilityPatch: (patch) => { pubUtilCache.invalidate('mainData'); dispatch({ type: 'UPDATE_UTILITY', payload: patch }); },
        onAssetPatch: (patch) => { pubUtilCache.invalidate('mainData'); dispatch({ type: 'PATCH_ASSET_LIVE', payload: patch }); },
        onEvent: handleLiveEvent,
    });
    // --- END UPDATE

    // --- UPDATE 7: uses fetchDataRef.current — never stale, no dep on fetchData
    const handleUtilityWsUpdate = useCallback((wsData) => {
        if (wsData && wsData.type === 'utility_status_update') {
            logger.log('utility:wsUpdate', wsData);
            pubUtilCache.invalidate('mainData');
            // --- UPDATE E: coalesced reload (was one fetch per message)
            live.scheduleRefresh('utility status');
            if (wsData.data || wsData.utility) {
                const updatedUtil = wsData.data || wsData.utility;
                toast({ title: 'Live System Alert', description: `Utility status changed to ${updatedUtil.status?.replace(/_/g, ' ')}.`, status: 'info', duration: 4000, isClosable: true, position: 'top-right' });
            }
        }
    }, [logger, toast, live.scheduleRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

    // ──────────────────────────────────────────────────────────────────────────
    // --- UPDATE 4D: HANDLERS — SUBSURFACE CLEARANCE
    // ──────────────────────────────────────────────────────────────────────────

    /**
     * Arms/disarms the inspector. Arming force-mounts the lazy GIS map and
     * scrolls it into view, otherwise the toggle appears to do nothing when the
     * map hasn't crossed the IntersectionObserver threshold yet.
     */
    const handleToggleClearanceInspector = useCallback(() => {
        setIsInspectingClearance(prev => {
            const next = !prev;
            logger.log('clearance:inspector', { armed: next });
            if (next) {
                setForceMapMount(true);
                setActiveTab('map'); // --- UPDATE (Public Utilities v2): the map lives on its own tab
                requestAnimationFrame(() => {
                    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            } else {
                spatialService.abortAll();
                dispatch({ type: 'CLEAR_CLEARANCE' });
            }
            return next;
        });
    }, [logger, mapSectionRef]);

    // The real implementation. Kept out of the prop identity so the memoised
    // GisMapView never holds a stale copy (see C in the header block).
    const clearanceCompleteImpl = useCallback((result, coords) => {
        logger.log(
            'clearance:result',
            {
                status: result?.status,
                severity: result?.severity,
                evaluationId: result?.evaluationId,
                lat: coords?.lat,
                lng: coords?.lng,
                utilityHits: result?.utilityCollisions?.length ?? 0,
                permitHits: result?.temporalConflicts?.length ?? 0,
            },
            result?.status === CLEARANCE_STATUS.CLEAR ? 'success' : 'warning'
        );

        // NOT written to pubUtilCache — see E in the header block.
        dispatch({ type: 'SET_CLEARANCE', payload: { result, lat: coords?.lat, lng: coords?.lng, at: Date.now() } });

        const t = clearanceToastForResult(result);
        if (t) {
            toast({
                ...t,
                duration: t.status === 'success' ? 3000 : 8000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }, [logger, toast]);

    useEffect(() => { clearanceHandlerRef.current = clearanceCompleteImpl; }, [clearanceCompleteImpl]);

    // Reference-stable forever — safe to hand to a memoised child.
    const handleClearanceInspectionComplete = useCallback((result, coords) => {
        clearanceHandlerRef.current?.(result, coords);
    }, []);

    const handleDismissClearance = useCallback(() => {
        dispatch({ type: 'CLEAR_CLEARANCE' });
    }, []);
    // --- END UPDATE 4D

    // ──────────────────────────────────────────────────────────────────────────
    // HANDLERS — ASSET
    // ──────────────────────────────────────────────────────────────────────────
    const handleSelectAsset = useCallback((assetOrObj) => {
        const assetId = assetOrObj ? (assetOrObj.id || assetOrObj) : null;
        logger.log('asset:select', { assetId });
        setSelectedAssetId(prev => (prev === assetId ? null : assetId));
        setEditingAsset(prev => {
            if (prev?.id === assetId) return null;
            return typeof assetOrObj === 'object' ? assetOrObj : null;
        });
    }, [logger]);

    const handleAddAsset = useCallback(() => {
        logger.log('asset:addOpen');
        setEditingAsset(null);
        setSelectedAssetId(null);
        onAssetModalOpen();
    }, [onAssetModalOpen, logger]);

    const handleEditAsset = useCallback((asset) => {
        logger.log('asset:editOpen', { id: asset?.id });
        setEditingAsset(asset);
        setSelectedAssetId(asset.id);
        onAssetModalOpen();
    }, [onAssetModalOpen, logger]);

    // --- UPDATE: AI panel actions
    // --- UPDATE F: always on — insights feed utility cards, failure triage and the AI panel
    const aiInsights = useAIInsights(AI_EMPTY_WOS, AI_EMPTY_STATS, { enabled: true, autoRefresh: true });
    const handleAiOpenAsset = useCallback((asset) => {
        if (!asset) return;
        const real = assetById.get(asset.id) || asset;
        if (selectedAssetId !== real.id) handleSelectAsset(real); // select only — never toggle off
        // --- UPDATE (Public Utilities v2): open the Asset 360 drawer instead of scrolling
        setDrawer({ asset: real, tab: 'overview', scheduleId: null });
        // --- END UPDATE
    }, [assetById, selectedAssetId, handleSelectAsset]);
    const handleAiEditAsset = useCallback((asset) => {
        if (!asset || !canEditAssets) return;
        handleEditAsset(assetById.get(asset.id) || asset);
    }, [assetById, canEditAssets, handleEditAsset]);
    // --- END UPDATE

    const handleReportFailure = useCallback((asset) => {
        logger.log('asset:reportFailure', { id: asset?.id });
        setFailureReportAsset(asset);
        onFailureModalOpen();
    }, [onFailureModalOpen, logger]);

    // --- UPDATE H: Report Failure submission (the modal had no onSubmit before)
    const handleSubmitFailureReport = useCallback(async (report) => {
        const asset = failureReportAsset;
        const assetId = report?.assetId ?? asset?.id;
        if (assetId == null || assetId === '') throw new Error('No asset selected');
        const newStatus = FAILURE_STATUS[report.severity] || 'maintenance_required';
        const previousStatus = assetById.get(assetId)?.status;
        logger.log('asset:failureReport', { assetId, severity: report.severity, category: report.category, ai: report.ai_triage });

        dispatch({ type: 'UPDATE_ASSET', payload: { id: assetId, status: newStatus } }); // optimistic
        try {
            await logger.trackNetwork('asset:failureStatus', AssetService.updateAsset(assetId, { status: newStatus }));
        } catch (err) {
            dispatch({ type: 'UPDATE_ASSET', payload: { id: assetId, status: previousStatus } }); // rollback
            throw err;
        }
        // Failure record in the maintenance history — the failure model learns from these.
        try {
            // --- UPDATE (Public Utilities v2): lifecycle API (category "failure") — shows in the asset
            // history, counts as a failure for the risk score and the RUL training data
            await logger.trackNetwork('asset:failureLog', logLifecycleMaintenance(assetId, {
                category: 'failure',
                title: `Failure reported · ${String(report.severity || '').toUpperCase()} · ${report.category || 'general'}`.slice(0, 255),
                notes: [report.description, report.location && `Location: ${report.location}`, report.urgentRepair && 'Urgent repair requested',
                    report.ai_triage && `AI triage: ${report.ai_triage.severity}/${report.ai_triage.category} (${report.ai_triage.confidence})`].filter(Boolean).join(' · '),
                performed_by_name: report.reportedBy || undefined,
                source: 'failure_report',
            }));
            // --- END UPDATE
        } catch (err) {
            logger.log('asset:failureLog_error (status already saved)', { error: err.message }, 'warning');
        }
        if (assetId === selectedAssetId) fetchMaintenanceHistory(selectedAssetId);
        pubUtilCache.invalidate('mainData');
        // Tell the Work Orders page / other tabs (a failure usually creates or changes work orders)
        publishBus(BUS_EVENTS.ASSET_CHANGED, { action: 'failure_reported', id: assetId, status: newStatus, severity: report.severity, origin: 'public_utilities_live' }); // --- UPDATE (v3): origin → this page ignores its own echo
        toast({ title: 'Failure reported', description: `${report.assetName || 'Asset'} marked ${newStatus.replace(/_/g, ' ')}.`, status: report.severity === 'critical' ? 'error' : 'warning', duration: 4000, isClosable: true });
    }, [failureReportAsset, assetById, selectedAssetId, fetchMaintenanceHistory, toast, logger]);

    // --- UPDATE G: AI view for each utility card
    const assetsByName = useMemo(() => {
        const m = new Map();
        for (const a of assets) if (a.name) m.set(String(a.name).toLowerCase(), a);
        return m;
    }, [assets]);
    const getUtilityAi = useCallback((utility) => {
        const affected = (utility.affected_assets || []).map(n => assetsByName.get(String(n).toLowerCase())).filter(Boolean);
        const atRiskAssets = affected.filter(a => getAssetAnalysis(a).health < 60).length;
        const related = aiInsights.insights.filter(ins =>
            (ins.utility_id != null && String(ins.utility_id) === String(utility.id)) ||
            (ins.utility && ins.utility === utility.name) ||
            affected.some(a => String(ins.asset_id ?? ins.actionPayload?.assetId) === String(a.id)));
        const status = String(utility.status || '').toLowerCase();
        const summary = ['partial_outage', 'full_outage', 'critical', 'warning', 'degraded'].includes(status) && affected.length
            ? `${affected.length} linked asset${affected.length === 1 ? '' : 's'} — check these first.`
            : null;
        return { insights: related, atRiskAssets, summary };
    }, [assetsByName, aiInsights.insights]);
    // --- END UPDATE

    const handleDeleteAsset = useCallback(async (assetId) => {
        if (!window.confirm(`Are you sure you want to delete asset ${assetId}?`)) return;
        logger.log('asset:deleting', { assetId });

        // --- UPDATE 4: optimistic remove — UI reflects deletion immediately.
        //   The user sees the asset disappear before the network request resolves.
        //   On failure, the asset is restored from the snapshot.
        const previousAssets = data.assets; // snapshot for rollback
        dispatch({ type: 'REMOVE_ASSET', payload: assetId });
        if (selectedAssetId === assetId) { setSelectedAssetId(null); setEditingAsset(null); }

        try {
            await logger.trackNetwork('asset:delete', AssetService.deleteAsset(assetId));
            toast({ title: 'Asset Deleted', description: `Asset ${assetId} has been deleted.`, status: 'success', duration: 3000, isClosable: true });
            pubUtilCache.invalidate('mainData');
            fetchData(); // background consistency sync
        } catch (err) {
            // --- UPDATE 4: rollback optimistic update on failure
            logger.log('asset:delete_error — rolling back', { error: err.message }, 'error');
            dispatch({ type: 'SET_ASSETS', payload: previousAssets });
            if (selectedAssetId === assetId) setSelectedAssetId(assetId);
            toast({ title: 'Deletion Failed', description: err.message || `Failed to delete asset ${assetId}.`, status: 'error', duration: 5000, isClosable: true });
        }
    }, [toast, fetchData, selectedAssetId, logger, data.assets]);

    // --- UPDATE (edit-only fix): the asset form sends the record WITHOUT an id and passes the id as
    //   the 2nd argument, so `formData.id` was empty on every edit and the save went to createAsset —
    //   a duplicate asset was created each time. The id now comes from (in order) the form data,
    //   the 2nd argument, or the asset being edited; with an id it is ALWAYS an update.
    const handleAssetFormSubmit = useCallback(async (rawFormData, assetIdArg) => {
        const editId = rawFormData?.id ?? assetIdArg ?? editingAsset?.id ?? null;
        const formData = editId != null && editId !== '' ? { ...rawFormData, id: editId } : rawFormData;
        setIsSubmitting(true);
        logger.log('assetForm:submitting', { isEdit: !!formData.id });

        // --- UPDATE 4: for updates, apply the change optimistically in local state
        //   so the table reflects the edit before the network roundtrip completes.
        const isEdit = formData.id != null && formData.id !== '';
        // --- END UPDATE
        const previousAssets = data.assets;
        if (isEdit) dispatch({ type: 'UPDATE_ASSET', payload: formData });

        try {
            let savedAsset;
            if (isEdit) {
                savedAsset = await logger.trackNetwork('asset:update', AssetService.updateAsset(formData.id, formData));
                toast({ title: 'Asset Updated', description: `Asset ${formData.id} updated successfully.`, status: 'success', duration: 3000, isClosable: true });
                // Confirm with authoritative server data
                dispatch({ type: 'UPDATE_ASSET', payload: savedAsset });
            } else {
                savedAsset = await logger.trackNetwork('asset:create', AssetService.createAsset(formData));
                dispatch({ type: 'ADD_ASSET', payload: savedAsset });
                toast({ title: 'Asset Created', description: 'New asset created successfully.', status: 'success', duration: 3000, isClosable: true });
            }
            onAssetModalClose();
            setEditingAsset(savedAsset);
            setSelectedAssetId(savedAsset.id);
            pubUtilCache.invalidate('mainData');
            fetchData(); // background sync
        } catch (err) {
            logger.log('assetForm:error', { error: err.message }, 'error');
            // --- UPDATE 4: rollback optimistic update
            if (isEdit) dispatch({ type: 'SET_ASSETS', payload: previousAssets });
            // --- UPDATE (edit-only fix): rethrow so the form stays open and shows the error itself
            //   (it was swallowed, and the form then reported "Asset Updated")
            throw err;
        } finally {
            setIsSubmitting(false);
        }
    }, [toast, onAssetModalClose, fetchData, logger, data.assets, editingAsset]);

    // ──────────────────────────────────────────────────────────────────────────
    // HANDLERS — MAINTENANCE
    // ──────────────────────────────────────────────────────────────────────────
    const handleRefreshMaintenanceHistory = useCallback(() => { fetchMaintenanceHistory(selectedAssetId); }, [selectedAssetId, fetchMaintenanceHistory]);

    const handleAddMaintenanceLog = useCallback(async (logData) => {
        logger.log('maintenance:creating', { asset_id: logData?.asset_id });
        try {
            await logger.trackNetwork('maintenance:create', AssetMaintenanceService.createAssetMaintenance(logData));
            toast({ title: 'Maintenance Logged', description: 'Maintenance record created successfully.', status: 'success', duration: 3000, isClosable: true });
            if (logData.asset_id === selectedAssetId) fetchMaintenanceHistory(selectedAssetId);
        } catch (err) {
            logger.log('maintenance:create_error', { error: err.message }, 'error');
            throw err;
        }
    }, [selectedAssetId, fetchMaintenanceHistory, toast, logger]);

    const handleChangeStatus = useCallback(async (assetId, newStatus) => {
        logger.log('asset:changeStatus', { assetId, newStatus });

        // --- UPDATE 4: optimistic status update — row in the table reflects
        //   the new status before the PATCH response arrives.
        const previousStatus = assetById.get(assetId)?.status; // O(1) via Map
        dispatch({ type: 'UPDATE_ASSET', payload: { id: assetId, status: newStatus } });

        try {
            await logger.trackNetwork('asset:statusUpdate', AssetService.updateAsset(assetId, { status: newStatus }));
            toast({ title: 'Status Updated', description: `Asset status changed to ${newStatus}.`, status: 'success', duration: 2000, isClosable: true });
            pubUtilCache.invalidate('mainData');
            fetchData();
        } catch (err) {
            // --- UPDATE 4: rollback on failure
            logger.log('asset:changeStatus_error — rolling back', { error: err.message }, 'error');
            dispatch({ type: 'UPDATE_ASSET', payload: { id: assetId, status: previousStatus } });
            toast({ title: 'Update Failed', description: err.message || 'Failed to update asset status.', status: 'error', duration: 5000, isClosable: true });
        }
    }, [toast, fetchData, logger, assetById]);

    const handleFetchAssetDetails = useCallback(async (assetId) => {
        try {
            const logs = await AssetMaintenanceService.fetchAssetMaintenanceHistory(assetId);
            // --- UPDATE 3: O(1) lookup replaces O(n) sensors.filter()
            const assetSensors = sensorsByAsset.get(assetId) || [];
            return { maintenanceLogs: logs, workOrders: [], sensors: assetSensors, alerts: [] };
        } catch (err) {
            console.error('Error fetching asset details:', err);
            throw new Error('Failed to load asset details.');
        }
    }, [sensorsByAsset]);

    // ──────────────────────────────────────────────────────────────────────────
    // --- UPDATE (Public Utilities v2): INTELLIGENCE, TABS, ASSET 360
    // ──────────────────────────────────────────────────────────────────────────
    const canAdminPu = canAdminister(currentUser?.role);
    const reloadAssets = useCallback(() => {
        // the asset service keeps its own 5-minute list cache — flush it so imports / bulk edits show at once
        try { AssetService.flushCache?.(); } catch (_) { /* older assetService without flushCache */ }
        pubUtilCache.invalidate('mainData');
        fetchDataRef.current?.(true);
    }, []);
    const intel = usePublicUtilitiesIntel({ enabled: true, onAssetsChanged: reloadAssets });

    const goTab = useCallback((tab) => {
        if (!PU_TABS.includes(tab)) return;
        setActiveTab(tab);
    }, []);
    useEffect(() => {
        setVisitedTabs((prev) => (prev.has(activeTab) ? prev : new Set([...prev, activeTab])));
        try {
            const url = new URL(window.location.href);
            if (activeTab === 'overview') url.searchParams.delete('tab'); else url.searchParams.set('tab', activeTab);
            window.history.replaceState(window.history.state, '', url.toString());
        } catch (_) { /* ignore */ }
    }, [activeTab]);

    const openAsset360 = useCallback((asset, tab = 'overview', scheduleId = null) => {
        if (!asset) return;
        const real = assetById.get(asset.id) || asset;
        setDrawer({ asset: real, tab, scheduleId });
    }, [assetById]);
    const closeAsset360 = useCallback(() => setDrawer((d) => ({ ...d, asset: null })), []);

    // --- UPDATE (Public Utilities v3): the map shows only Public Utilities data (no roads) and stays live
    const puMap = usePuMapLayer({ enabled: shouldMountMap, version: live.puVersion });
    const hasPuLayer = puMap.available && !!puMap.data; // backend v3 not deployed → classic markers as before
    const [puColorBy, setPuColorBy] = useState('type');
    const [puVisibleTypes, setPuVisibleTypes] = useState(() => new Set());
    const [showPuZones, setShowPuZones] = useState(true);
    const [mapLayers, setMapLayers] = useState({ puAssets: true, pipelines: true, sensors: true, anomalies: true });
    const [mapFullscreen, setMapFullscreen] = useState(false);
    const puTypeRows = useMemo(() => (puMap.data ? puLegend(puMap.data, 'type') : PU_EMPTY), [puMap.data]);
    const onTogglePuType = useCallback((key) => setPuVisibleTypes((prev) => {
        const next = new Set(prev.size ? prev : puTypeRows.map((t) => t.key));
        if (next.has(key)) next.delete(key); else next.add(key);
        return next.size === puTypeRows.length ? new Set() : next;
    }), [puTypeRows]);
    const onShowAllPuTypes = useCallback(() => setPuVisibleTypes(new Set()), []);
    const onTogglePuZones = useCallback(() => setShowPuZones((v) => !v), []);
    const onToggleMapLayer = useCallback((k) => setMapLayers((l) => ({ ...l, [k]: !l[k] })), []);
    const onToggleMapFullscreen = useCallback(() => setMapFullscreen((v) => !v), []);
    const onPuFeatureSelect = useCallback((feature) => {
        if (!feature) return;
        setSelectedAssetId(feature.id);
        const real = assetById.get(feature.id);
        setDrawer({ asset: real || { ...feature, latitude: feature.lat, longitude: feature.lng, asset_code: feature.code }, tab: 'overview', scheduleId: null });
    }, [assetById]);
    // --- UPDATE (v3): the classic view's calendar shows and edits real inspection schedules
    const [calendarAppointments, setCalendarAppointments] = useState(PU_EMPTY);
    const calendarOpenRef = useRef(false);
    const loadCalendar = useCallback(async () => {
        calendarOpenRef.current = true;
        try {
            const r = await puApi.getDueInspections({ days: 365, limit: 1000 });
            setCalendarAppointments((r?.rows || []).map((s) => ({
                id: s.id, asset_id: s.asset_id, title: s.title || `Inspection · ${s.asset_code || s.asset_name || ''}`.trim(),
                description: [s.asset_code, s.asset_name, s.constituency].filter(Boolean).join(' · '),
                scheduled_date: s.next_due_date, scheduled_time: '', maintenance_type: 'inspection',
                priority: s.state === 'overdue' ? 'critical' : s.state === 'due_this_week' ? 'high' : 'medium',
                status: 'scheduled', assigned_user_id: s.assigned_user_id || '', assigned_user_name: s.assigned_user_name || s.assigned_manager_name || null,
            })));
        } catch (e) { logger.log('calendar:load_error', { error: e.message }, 'warning'); }
    }, [logger]);
    useEffect(() => { if (calendarOpenRef.current) loadCalendar(); }, [live.puVersion, loadCalendar]);
    const onScheduleAppointment = useCallback(async (a) => {
        if (!a.asset_id) throw new Error('Choose an asset first.');
        await puApi.createSchedule(a.asset_id, { title: a.title || null, next_due_date: a.scheduled_date, frequency_days: null, assigned_user_id: a.assigned_user_id || null });
        intel.refreshSoon(); loadCalendar();
    }, [intel, loadCalendar]);
    const onUpdateAppointment = useCallback(async (id, a) => {
        await puApi.updateSchedule(id, { title: a.title || null, next_due_date: a.scheduled_date, assigned_user_id: a.assigned_user_id || null });
        intel.refreshSoon(); loadCalendar();
    }, [intel, loadCalendar]);
    const onDeleteAppointment = useCallback(async (id) => { await puApi.deleteSchedule(id); intel.refreshSoon(); loadCalendar(); }, [intel, loadCalendar]);
    // --- END UPDATE
    useEffect(() => {
        if (!mapFullscreen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setMapFullscreen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mapFullscreen]);
    // --- END UPDATE
    const afterLifecycleChange = useCallback(() => { intel.refreshSoon(); reloadAssets(); }, [intel, reloadAssets]);

    // stable callbacks for the memoised tabs (inline arrows would re-render every hidden tab)
    const onOpenAssetTab = useCallback((a) => openAsset360(a), [openAsset360]);
    const onInspectFromBoard = useCallback((a, scheduleId) => openAsset360(a, canEditAssets || currentUser?.role === 'inspector' ? 'inspect' : 'overview', scheduleId), [openAsset360, canEditAssets, currentUser?.role]);
    const onImportedCb = useCallback(() => { reloadAssets(); intel.refreshSoon(); }, [reloadAssets, intel]);
    const onOpenRegistryCb = useCallback(() => goTab('registry'), [goTab]);

    /** Runs a recommendation / tile action from the Overview and Intelligence tabs. */
    const handleIntelAction = useCallback((action) => {
        if (!action) return;
        switch (action.type) {
            case 'tab':
                if (action.tab === 'inspections' && action.filter) setInspectionsFilter({ ...action.filter, at: Date.now() });
                goTab(action.tab);
                break;
            case 'filter':
                setRegistryFilter({ ...action, at: Date.now() });
                goTab('registry');
                break;
            case 'bulk_schedule':
                setBulkRequest({ only_unscheduled: true, at: Date.now() });
                goTab('inspections');
                break;
            case 'rezone':
            case 'link':
                goTab('zones');
                break;
            default:
                break;
        }
    }, [goTab]);
    const onFilterManagerAssets = useCallback((m) => handleIntelAction({ type: 'filter', manager_id: m.id }), [handleIntelAction]);
    const onFilterZoneAssets = useCallback((z) => handleIntelAction({ type: 'filter', zone_key: z.key }), [handleIntelAction]);
    const onIntelFilter = useCallback((f) => handleIntelAction({ type: 'filter', ...f }), [handleIntelAction]);
    // --- END UPDATE

    // ──────────────────────────────────────────────────────────────────────────
    // --- UPDATE 6: MEMOISED SECTION ACTIONS
    // Inline JSX creates new object references every render, defeating React.memo
    // on SectionCard. Each memoised action element only changes when its own
    // dependency (isLoadingData, canEditAssets, etc.) actually changes.
    // ──────────────────────────────────────────────────────────────────────────
    const refreshAction = useMemo(() => (
        <IconButton icon={<Icon as={MdRefresh} />} size="sm" variant="ghost" onClick={handleManualRefresh} isLoading={isLoadingData} aria-label="Refresh" borderRadius="full" />
    ), [handleManualRefresh, isLoadingData]);

    const addAssetAction = useMemo(() => canEditAssets ? (
        <Button size="sm" colorScheme="teal" leftIcon={<Icon as={MdAdd} />} onClick={handleAddAsset} borderRadius="full" shadow="sm">Add Asset</Button>
    ) : null, [canEditAssets, handleAddAsset]);

    // --- UPDATE 4D: map section header actions (inspector toggle + refresh)
    const clearanceToggleAction = useMemo(() => canRunClearanceChecks ? (
        <Tooltip label={isInspectingClearance ? 'Disarm subsurface inspector' : 'Arm 4D subsurface inspector — click the map to scan for buried utilities and permits'} hasArrow>
            <Button
                size="sm"
                variant={isInspectingClearance ? 'solid' : 'outline'}
                colorScheme={isInspectingClearance ? 'orange' : 'gray'}
                leftIcon={<Icon as={MdGpsFixed} />}
                onClick={handleToggleClearanceInspector}
                borderRadius="full"
                shadow="sm"
            >
                {isInspectingClearance ? 'Inspecting' : '4D Clearance'}
            </Button>
        </Tooltip>
    ) : null, [canRunClearanceChecks, isInspectingClearance, handleToggleClearanceInspector]);

    const mapSectionActions = useMemo(() => (
        <>{clearanceToggleAction}{refreshAction}</>
    ), [clearanceToggleAction, refreshAction]);
    // --- END UPDATE 4D

    // --- UPDATE (Public Utilities v2): the existing management view, fed the registry's filtered list
    const renderClassicAssets = useCallback((filteredAssets) => (
        <SectionCard icon={MdOutlineInventory2} title="Asset Management" subtitle={`${filteredAssets.length} assets in the current filter`} index={3} actions={addAssetAction} isMounted noPadding {...sectionCardProps}>
            {isLoadingData && !assets.length ? <Box p={6}><SectionSkeleton lines={6} /></Box> : (
                <Suspense fallback={<Box p={6}><SectionSkeleton lines={6} /></Box>}>
                    <AssetManagementIntegration
                        assets={filteredAssets}
                        users={users}
                        sensors={sensors}
                        selectedAssetId={selectedAssetId}
                        isAdmin={isAdmin}
                        onSelectAsset={(id) => handleSelectAsset(assetById.get(id) || id)}
                        onAddAsset={handleAddAsset}
                        onEditAsset={handleEditAsset}
                        onSaveAsset={handleAssetFormSubmit} /* --- UPDATE (edit-only fix): table's form saves directly */
                        onDeleteAsset={handleDeleteAsset}
                        onReportFailure={handleReportFailure}
                        onAddMaintenanceLog={handleAddMaintenanceLog}
                        onFetchAssetDetails={handleFetchAssetDetails}
                        enableDetailsModal={true}
                        enableCalendar={true}
                        enableReports={true}
                        loading={isLoadingData}
                        maintenanceInterval={90}
                        showCost={isAdmin}
                        managers={intel.managers} /* --- UPDATE (v3): managed-by column */
                        onOpenAsset360={onOpenAssetTab} /* --- UPDATE (v3): Asset 360 on every row */
                        appointments={calendarAppointments} /* --- UPDATE (v3): calendar = inspection schedules */
                        onCalendarOpen={loadCalendar}
                        puOverview={intel.overview}
                        onScheduleAppointment={onScheduleAppointment}
                        onUpdateAppointment={onUpdateAppointment}
                        onDeleteAppointment={onDeleteAppointment}
                        calendarTitle="Inspection & maintenance calendar"
                        calendarSubtitle="Every scheduled inspection — the same schedules as the Inspections tab and the tech app"
                    />
                </Suspense>
            )}
        </SectionCard>
    ), [sectionCardProps, addAssetAction, isLoadingData, assets.length, users, sensors, selectedAssetId, isAdmin, handleSelectAsset, assetById,
        handleAddAsset, handleEditAsset, handleDeleteAsset, handleReportFailure, handleAddMaintenanceLog, handleFetchAssetDetails,
        intel.managers, intel.overview, onOpenAssetTab, calendarAppointments, loadCalendar, onScheduleAppointment, onUpdateAppointment, onDeleteAppointment]); // --- UPDATE (v3)
    // --- END UPDATE

    // ──────────────────────────────────────────────────────────────────────────
    // RENDER
    // ──────────────────────────────────────────────────────────────────────────
    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }} minH="100vh">
            <VStack spacing={6} align="stretch">

                {/* PAGE HEADER */}
                <MotionBox initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                    <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} mb={1}>
                        <HStack spacing={3}>
                            <Box p={2.5} bgGradient={headingGradient} borderRadius="xl" shadow="md">
                                <Icon as={MdWaterDrop} boxSize={6} color="white" />
                            </Box>
                            <VStack align="start" spacing={0}>
                                <Heading size="lg" color={textColor} letterSpacing="-0.03em">Public Utilities</Heading>
                                <HStack spacing={2} fontSize="xs" color={grayColor}>
                                    <Text>{pageStats.totalUtilities} utilities</Text><Text>•</Text>
                                    <Text>{pageStats.totalAssets} assets</Text>
                                    {lastRefresh && <><Text>•</Text><Text>Updated {lastRefresh.toLocaleTimeString()}</Text></>}
                                    {/* --- UPDATE D: live feed badge */}
                                    <Text>•</Text>
                                    <Popover trigger="click" placement="bottom-start" isLazy>
                                        <PopoverTrigger>
                                            <Badge as="button" type="button" colorScheme={live.isConnected ? 'green' : 'gray'} borderRadius="full" px={2}
                                                display="inline-flex" alignItems="center" gap={1} cursor="pointer"
                                                aria-label={`Live feed ${live.isConnected ? 'connected' : 'offline'}, ${live.eventCount} events`}>
                                                <Icon as={MdFiberManualRecord} boxSize={2} />
                                                {live.isConnected ? 'Live' : 'Live offline'}{live.eventCount ? ` · ${live.eventCount}` : ''}
                                                {live.pendingRefresh && <Spinner size="xs" ml={1} />}
                                            </Badge>
                                        </PopoverTrigger>
                                        <Portal>
                                            <PopoverContent w="320px" borderRadius="xl" bg={cardBg}>
                                                <PopoverArrow bg={cardBg} />
                                                <PopoverBody p={3}>
                                                    <Text fontSize="sm" fontWeight="800" mb={2}>Live events</Text>
                                                    {live.recentEvents.length === 0 ? (
                                                        <Text fontSize="xs" color={grayColor}>
                                                            {live.isConnected ? 'Connected — waiting for updates from utilities, assets, sensors and the AI.' : 'Not connected — data still refreshes every few minutes.'}
                                                        </Text>
                                                    ) : (
                                                        <VStack align="stretch" spacing={1.5} maxH="260px" overflowY="auto">
                                                            {live.recentEvents.map(e => (
                                                                <HStack key={e.id} spacing={2} fontSize="xs" align="flex-start">
                                                                    <Badge fontSize="9px" colorScheme={{ utility: 'teal', asset: 'blue', sensor: 'orange', work: 'purple', ai: 'pink' }[e.kind]}>{e.kind}</Badge>
                                                                    <Box flex={1} minW={0}>
                                                                        <Text fontWeight="700" noOfLines={1}>{e.label}{e.status ? ` → ${String(e.status).replace(/_/g, ' ')}` : ''}</Text>
                                                                        <Text color={grayColor} noOfLines={1}>{e.title || (e.assetId != null ? `Asset ${e.assetId}` : e.utilityId != null ? `Utility ${e.utilityId}` : '')} · {new Date(e.at).toLocaleTimeString()}</Text>
                                                                    </Box>
                                                                </HStack>
                                                            ))}
                                                        </VStack>
                                                    )}
                                                </PopoverBody>
                                            </PopoverContent>
                                        </Portal>
                                    </Popover>
                                    {/* --- END UPDATE */}
                                </HStack>
                            </VStack>
                        </HStack>
                        <HStack spacing={2}>
                            {(process.env.NODE_ENV === 'development' || window.__DEBUG__) && (
                                <Tooltip label="Toggle Debug Panel" hasArrow>
                                    <IconButton icon={<Icon as={MdBugReport} />} size="sm" variant={showDebug ? 'solid' : 'ghost'} colorScheme={showDebug ? 'purple' : 'gray'} onClick={toggleDebug} aria-label="Debug" borderRadius="full" />
                                </Tooltip>
                            )}
                            {/* --- UPDATE 6: refreshAction is memoised — no new reference per render */}
                            <Tooltip label="Refresh All Data" hasArrow>{refreshAction}</Tooltip>
                        </HStack>
                    </Flex>
                </MotionBox>

                {/* QUICK STATS */}
                <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12, duration: 0.35 }}>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <StatCard label="Utilities" value={`${pageStats.utilActive}/${pageStats.totalUtilities}`} icon={MdDashboard} iconColor="teal.600" iconBg="teal.100" cardBg={cardBg} borderColor={borderColor} textColor={textColor} grayColor={grayColor} />
                        <StatCard label="Operational" value={pageStats.operational} icon={MdCheckCircle} iconColor="green.600" iconBg="green.100" valueColor={statOperationalColor} cardBg={cardBg} borderColor={borderColor} textColor={textColor} grayColor={grayColor} />
                        <StatCard label="Critical" value={pageStats.critical} icon={MdError} iconColor="red.600" iconBg="red.100" valueColor={pageStats.critical > 0 ? statCriticalColor : textColor} cardBg={cardBg} borderColor={borderColor} textColor={textColor} grayColor={grayColor} />
                        <StatCard label="Sensors" value={pageStats.sensorCount} icon={MdSensors} iconColor="blue.600" iconBg="blue.100" cardBg={cardBg} borderColor={borderColor} textColor={textColor} grayColor={grayColor} />
                    </SimpleGrid>
                </MotionBox>

                {/* DEBUG PANEL */}
                <Collapse in={showDebug} animateOpacity>
                    <Box bg={debugBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor="purple.600" shadow="lg" />
                </Collapse>

                {/* Global Error Alert */}
                <AnimatePresence>
                    {dataError && (
                        <MotionBox key="data-error" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}>
                            <Alert status="error" borderRadius="xl" variant="subtle" shadow="sm">
                                <AlertIcon />
                                <Box flex={1}><AlertTitle fontSize="sm">Data Loading Issue</AlertTitle><AlertDescription fontSize="xs">{dataError}</AlertDescription></Box>
                                <IconButton icon={<Icon as={MdClose} />} size="xs" variant="ghost" onClick={() => setDataError(null)} aria-label="Dismiss" borderRadius="full" />
                            </Alert>
                        </MotionBox>
                    )}
                </AnimatePresence>

                {/* --- UPDATE 4D: PERSISTENT CLEARANCE BANNER --------------------
                    A toast disappears in 8 seconds. A crew standing over a gas
                    main needs the warning to stay on screen until dismissed. */}
                <AnimatePresence>
                    {lastClearance?.result && !spatialService.isIgnorable(lastClearance.result) && (
                        <MotionBox key="clearance-banner" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}>
                            <Alert
                                status={clearanceIsBlocking ? (lastClearance.result.status === CLEARANCE_STATUS.INDETERMINATE ? 'error' : 'warning') : 'success'}
                                borderRadius="xl"
                                variant="subtle"
                                shadow="sm"
                                alignItems="flex-start"
                            >
                                <AlertIcon as={clearanceIsBlocking ? MdWarningAmber : MdVerifiedUser} />
                                <Box flex={1}>
                                    <HStack spacing={2} flexWrap="wrap">
                                        <AlertTitle fontSize="sm">
                                            {clearanceIsBlocking ? 'Subsurface Clearance Required' : 'Subsurface Clearance Verified'}
                                        </AlertTitle>
                                        <Badge colorScheme={clearanceIsBlocking ? 'red' : 'green'} borderRadius="full" px={2}>
                                            {lastClearance.result.status}
                                        </Badge>
                                        {lastClearance.result.severity && lastClearance.result.severity !== 'NONE' && (
                                            <Badge variant="outline" colorScheme={clearanceIsBlocking ? 'red' : 'green'} borderRadius="full" px={2}>
                                                {lastClearance.result.severity}
                                            </Badge>
                                        )}
                                    </HStack>
                                    <AlertDescription fontSize="xs" display="block" mt={1}
                                        color={clearanceIsBlocking ? clearanceBlockColor : clearanceClearColor}>
                                        {spatialService.summarize(lastClearance.result)}
                                    </AlertDescription>
                                    <Text fontSize="10px" color={grayColor} mt={1}>
                                        {typeof lastClearance.lat === 'number' && typeof lastClearance.lng === 'number'
                                            ? `${lastClearance.lat.toFixed(5)}, ${lastClearance.lng.toFixed(5)}`
                                            : 'coordinates unavailable'}
                                        {lastClearance.result.evaluationId ? ` · audit ${lastClearance.result.evaluationId}` : ''}
                                    </Text>
                                </Box>
                                <IconButton icon={<Icon as={MdClose} />} size="xs" variant="ghost" onClick={handleDismissClearance} aria-label="Dismiss clearance result" borderRadius="full" />
                            </Alert>
                        </MotionBox>
                    )}
                </AnimatePresence>
                {/* --- END UPDATE 4D */}

                {isLoadingData && <Progress size="xs" isIndeterminate colorScheme="teal" borderRadius="full" />}

                {/* --- UPDATE (Public Utilities v2): TABBED MODULE ------------------------------------------------
                    Each tab mounts on first visit (isLazy) and stays mounted (keepMounted) so the GIS map and
                    the registry keep their state when you switch back. */}
                <Tabs index={Math.max(0, PU_TABS.indexOf(activeTab))} onChange={(i) => goTab(PU_TABS[i])} isLazy lazyBehavior="keepMounted" colorScheme="teal" variant="soft-rounded">
                    <TabList overflowX="auto" overflowY="hidden" pb={1} gap={1} sx={{ scrollbarWidth: 'thin' }}>
                        <Tab flexShrink={0}><Icon as={MdSpaceDashboard} mr={1.5} />Overview</Tab>
                        <Tab flexShrink={0}><Icon as={MdViewList} mr={1.5} />Registry<Badge ml={2} borderRadius="full" colorScheme="teal" variant="subtle">{pageStats.totalAssets}</Badge></Tab>
                        <Tab flexShrink={0}><Icon as={MdMap} mr={1.5} />Map</Tab>
                        <Tab flexShrink={0}><Icon as={MdEventNote} mr={1.5} />Inspections{intel.overview?.totals?.overdue ? <Badge ml={2} borderRadius="full" colorScheme="red">{intel.overview.totals.overdue}</Badge> : null}</Tab>
                        <Tab flexShrink={0}><Icon as={MdGroups} mr={1.5} />Managers</Tab>
                        <Tab flexShrink={0}><Icon as={MdLayers} mr={1.5} />Zones</Tab>
                        <Tab flexShrink={0} isDisabled={!canAdminPu}><Icon as={MdUploadFile} mr={1.5} />Import</Tab>
                        <Tab flexShrink={0}><Icon as={MdPsychology} mr={1.5} />Intelligence{intel.overview?.recommendations?.length ? <Badge ml={2} borderRadius="full" colorScheme="purple">{intel.overview.recommendations.length}</Badge> : null}</Tab>
                    </TabList>
                    <TabPanels>
                        {/* OVERVIEW — KPIs, needs attention, AI, utility status, lifecycle cost */}
                        <TabPanel px={0}>
                            <PuOverviewTab intel={intel} onAction={handleIntelAction}>
                                <SectionCard
                                    icon={MdWaterDrop}
                                    title="Utility Status"
                                    subtitle={`${pageStats.utilActive} of ${pageStats.totalUtilities} operational`}
                                    index={0}
                                    actions={refreshAction}
                                    isMounted
                                    {...sectionCardProps}
                                >
                                    {isLoadingData && !utilities.length ? (
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            {[1, 2, 3].map(i => <Skeleton key={i} h="80px" borderRadius="xl" />)}
                                        </SimpleGrid>
                                    ) : (
                                        <UtilityStatusGrid
                                            utilities={utilities}
                                            canEditUtilities={canEditUtilities}
                                            onEditUtility={handleEditUtility}
                                            enableWebSocket={true}
                                            webSocketUrl={WS_URL}
                                            onStatusChange={handleUtilityWsUpdate}
                                            onRefresh={handleManualRefresh}
                                            getUtilityAi={getUtilityAi}
                                            liveConnected={live.isConnected}
                                        />
                                    )}
                                </SectionCard>
                                <Box ref={lifecycleSectionRef}>
                                    <SectionCard icon={MdAttachMoney} title="Lifecycle Cost Analysis" subtitle="TCO & Efficiency Insights" index={1} isMounted {...sectionCardProps}>
                                        {lifecycleInView ? (
                                            <Suspense fallback={<SectionSkeleton lines={6} />}>
                                                <LifecycleCostWidget dateRange={dateRange} />
                                            </Suspense>
                                        ) : <SectionSkeleton lines={6} />}
                                    </SectionCard>
                                </Box>
                            </PuOverviewTab>
                        </TabPanel>

                        {/* REGISTRY — fast table + the existing management view */}
                        <TabPanel px={0}>
                            <Box ref={assetSectionRef}>
                                <SectionErrorBoundary title="Asset registry failed to load" onError={(err) => logger.log('registry:crash', { error: err.message }, 'error')}>
                                    <Suspense fallback={<SectionSkeleton lines={8} />}>
                                        <AssetRegistryTab
                                            assets={assets}
                                            intel={intel}
                                            filterRequest={registryFilter}
                                            onOpenAsset={onOpenAssetTab}
                                            onAddAsset={canEditAssets ? handleAddAsset : undefined}
                                            onAssetsMutated={reloadAssets}
                                            canAdmin={canAdminPu}
                                            canEdit={canEditAssets}
                                            renderClassic={renderClassicAssets}
                                        />
                                    </Suspense>
                                </SectionErrorBoundary>
                            </Box>
                        </TabPanel>

                        {/* MAP — GIS + 4D subsurface clearance */}
                        <TabPanel px={0}>
                            <Box ref={mapSectionRef}>
                                <SectionCard icon={MdMap} title="Geographic Data Overview"
                                    subtitle={clearanceSubtitle}
                                    index={2}
                                    actions={mapSectionActions}
                                    isMounted
                                    {...sectionCardProps}
                                >
                                    {isInspectingClearance && (
                                        <Alert status="info" borderRadius="lg" mb={3} py={2} fontSize="xs" variant="left-accent">
                                            <AlertIcon boxSize={4} />
                                            Inspector armed — click any point on the map to scan for buried utilities and active excavation permits. Press Esc to clear the readout.
                                        </Alert>
                                    )}
                                    {/* --- UPDATE (v3): relative wrapper for the map controls; Esc / button for full screen */}
                                    <Box h={mapFullscreen ? '100vh' : { base: '420px', md: '600px' }} borderRadius={mapFullscreen ? 0 : 'xl'} overflow="hidden" borderWidth="1px" borderColor={borderColor}
                                        position={mapFullscreen ? 'fixed' : 'relative'} inset={mapFullscreen ? 0 : undefined} zIndex={mapFullscreen ? 1400 : undefined} bg={cardBg}
                                        data-testid="pu-map-box">
                                        {shouldMountMap && (
                                            <MapFilterBar
                                                visibleLayers={mapLayers}
                                                onToggleLayer={onToggleMapLayer}
                                                layerOptions={PU_MAP_LAYERS}
                                                isFullScreen={mapFullscreen}
                                                onToggleFullScreen={onToggleMapFullscreen}
                                                isInspectingClearance={isInspectingClearance}
                                                onToggleClearanceInspector={handleToggleClearanceInspector}
                                                showInspector={false} /* the inspector toggle stays in the section header */
                                                puColorBy={puColorBy}
                                                onPuColorBy={hasPuLayer ? setPuColorBy : undefined}
                                                puTypes={puTypeRows}
                                                puVisibleTypes={puVisibleTypes}
                                                onTogglePuType={hasPuLayer ? onTogglePuType : undefined}
                                                onShowAllPuTypes={onShowAllPuTypes}
                                                showPuZones={showPuZones}
                                                onTogglePuZones={hasPuLayer ? onTogglePuZones : undefined}
                                            />
                                        )}
                                    {/* --- END UPDATE */}
                                        {shouldMountMap ? (
                                            <SectionErrorBoundary
                                                title="GIS map failed to load"
                                                onError={(err) => logger.log('gisMap:crash', { error: err.message }, 'error')}
                                            >
                                                <Suspense fallback={<Flex justify="center" align="center" h="full"><VStack spacing={3}><Spinner size="lg" color="teal.500" thickness="3px" /><Text fontSize="sm" color={grayColor}>Loading GIS Map…</Text></VStack></Flex>}>
                                                    <GisMapView
                                                        /* --- UPDATE (v3): with the PU layer the classic markers are off — the canvas layer draws
                                                           only Public Utilities assets (no roads). Without it (backend not deployed) → as before */
                                                        assets={hasPuLayer ? PU_EMPTY : assets}
                                                        showAssetMarkers={!hasPuLayer}
                                                        puLayer={hasPuLayer && mapLayers.puAssets ? puMap.data : null}
                                                        puColorBy={puColorBy}
                                                        puVisibleTypes={puVisibleTypes}
                                                        showPuZones={showPuZones}
                                                        selectedPuId={selectedAssetId}
                                                        onPuFeatureSelect={onPuFeatureSelect}
                                                        liveConnected={live.isConnected}
                                                        showAnomalies={mapLayers.anomalies}
                                                        showSensorMarkers={mapLayers.sensors}
                                                        sensors={mapLayers.sensors ? sensors : PU_EMPTY}
                                                        pipelines={mapLayers.pipelines ? pipelines : PU_EMPTY}
                                                        /* --- END UPDATE */
                                                        selectedAsset={editingAsset}
                                                        onAssetSelect={handleSelectAsset}
                                                        issueLocations={issueLocations}
                                                        isLoadingData={isLoadingData}
                                                        dataError={dataError}
                                                        isInspectingClearance={isInspectingClearance}
                                                        onClearanceInspectionComplete={handleClearanceInspectionComplete}
                                                        clearanceWorkOrderId={null}
                                                        clearanceDefaultDepthM={1.5}
                                                    />
                                                </Suspense>
                                            </SectionErrorBoundary>
                                        ) : (
                                            <Flex justify="center" align="center" h="full" bg={cardBg}>
                                                <VStack spacing={3}><Spinner size="md" color="teal.400" /><Text fontSize="sm" color={grayColor}>Loading map…</Text></VStack>
                                            </Flex>
                                        )}
                                    </Box>
                                    {/* --- UPDATE (v3): what is on the map, live state, AI findings */}
                                    {hasPuLayer && (
                                        <Flex mt={2} gap={2} align="center" wrap="wrap" fontSize="xs" color={grayColor} data-testid="pu-map-status">
                                            <Text><b>{(puMap.data.total || 0).toLocaleString()}</b> Public Utilities assets on the map</Text>
                                            {puMap.data.no_gps > 0 && (
                                                <Button size="xs" variant="link" colorScheme="orange" onClick={() => handleIntelAction({ type: 'filter', unlocated: true })}>{puMap.data.no_gps} without GPS — fix</Button>
                                            )}
                                            <Badge colorScheme={live.isConnected ? 'green' : 'gray'} variant="subtle">{live.isConnected ? 'Live' : 'Live offline'}</Badge>
                                            {puMap.loading && <Spinner size="xs" />}
                                            {puMap.loadedAt && <Text>updated {new Date(puMap.loadedAt).toLocaleTimeString()}</Text>}
                                            {puMap.error && <Text color="orange.400">{puMap.error} (showing the last loaded map)</Text>}
                                            <Text ml="auto">Roads are on the Road Works map.</Text>
                                        </Flex>
                                    )}
                                    {hasPuLayer && <PuMapInsights layer={puMap.data} onColorBy={setPuColorBy} onAction={handleIntelAction} />}
                                    {/* --- END UPDATE */}
                                    {selectedAssetObj && (
                                        <Flex mt={3} p={3} borderWidth="1px" borderColor={borderColor} borderRadius="xl" align="center" gap={3} flexWrap="wrap">
                                            <Box flex={1} minW={0}>
                                                <Text fontSize="sm" fontWeight="700" noOfLines={1}>{selectedAssetObj.asset_code ? `${selectedAssetObj.asset_code} · ` : ''}{selectedAssetObj.name}</Text>
                                                <Text fontSize="xs" color={grayColor} noOfLines={1}>{selectedAssetObj.location_description || selectedAssetObj.type}</Text>
                                            </Box>
                                            <Button size="sm" colorScheme="teal" borderRadius="full" onClick={() => openAsset360(selectedAssetObj)}>Open asset 360</Button>
                                        </Flex>
                                    )}
                                </SectionCard>
                            </Box>
                        </TabPanel>

                        {/* INSPECTIONS — due board + bulk scheduling */}
                        <TabPanel px={0}>
                            <Suspense fallback={<SectionSkeleton lines={8} />}>
                                <InspectionsTab
                                    intel={intel}
                                    filterRequest={inspectionsFilter}
                                    bulkRequest={bulkRequest}
                                    canAdmin={canAdminPu}
                                    onInspect={onInspectFromBoard}
                                    onOpenAsset={onOpenAssetTab}
                                />
                            </Suspense>
                        </TabPanel>

                        {/* MANAGERS & CONTRACTORS */}
                        <TabPanel px={0}>
                            <Suspense fallback={<SectionSkeleton lines={8} />}>
                                <AssetManagersTab
                                    intel={intel}
                                    assets={assets}
                                    canAdmin={canAdminPu}
                                    onAssetsMutated={reloadAssets}
                                    onFilterAssets={onFilterManagerAssets}
                                />
                            </Suspense>
                        </TabPanel>

                        {/* ZONES (= CONSTITUENCIES) */}
                        <TabPanel px={0}>
                            <Suspense fallback={<SectionSkeleton lines={8} />}>
                                <ZonesTab intel={intel} canAdmin={canAdminPu} onAssetsMutated={reloadAssets}
                                    onFilterZone={onFilterZoneAssets} />
                            </Suspense>
                        </TabPanel>

                        {/* IMPORT */}
                        <TabPanel px={0}>
                            {canAdminPu ? (
                                <Suspense fallback={<SectionSkeleton lines={8} />}>
                                    <AssetImportWizard intel={intel} onImported={onImportedCb} onOpenRegistry={onOpenRegistryCb} />
                                </Suspense>
                            ) : (
                                <Alert status="info" borderRadius="xl"><AlertIcon />Imports are limited to administrators, managers, supervisors and office staff.</Alert>
                            )}
                        </TabPanel>

                        {/* INTELLIGENCE — explainable AI + existing AI panel + predictive maintenance */}
                        <TabPanel px={0}>
                            <Suspense fallback={<SectionSkeleton lines={8} />}>
                                <AssetIntelligenceTab intel={intel} onOpenAsset={onOpenAssetTab} onFilter={onIntelFilter}>
                                    <SectionCard icon={MdPsychology} title="AI Asset Intelligence"
                                        subtitle={aiInsights.insightCount
                                            ? `${aiInsights.insightCount} live AI insight${aiInsights.insightCount === 1 ? '' : 's'}${aiInsights.hasCritical ? ' · critical alerts' : ''}`
                                            : 'What the AI needs and what it sees'}
                                        index={5} isMounted {...sectionCardProps}>
                                        <SectionErrorBoundary title="AI asset intelligence failed to load"
                                            onError={(err) => logger.log('aiPanel:crash', { error: err.message }, 'error')}>
                                            <Suspense fallback={<SectionSkeleton lines={6} />}>
                                                <AssetAiIntelligencePanel
                                                    assets={assets}
                                                    sensorsByAsset={sensorsByAsset}
                                                    assetById={assetById}
                                                    insights={aiInsights.insights}
                                                    isGenerating={aiInsights.isGenerating}
                                                    lastGenerated={aiInsights.lastGenerated}
                                                    wsStatus={aiInsights.wsStatus}
                                                    onRefresh={aiInsights.refresh}
                                                    onSnooze={aiInsights.snoozeInsight}
                                                    onEditAsset={canEditAssets ? handleAiEditAsset : undefined}
                                                    onSelectAsset={handleAiOpenAsset}
                                                    onReportFailure={handleReportFailure}
                                                />
                                            </Suspense>
                                        </SectionErrorBoundary>
                                    </SectionCard>
                                    <SectionCard icon={MdInsights} title="Predictive Maintenance" subtitle="AI-powered failure predictions" index={4} isMounted {...sectionCardProps}>
                                        <Suspense fallback={<SectionSkeleton lines={5} />}>
                                            <PredictiveMaintenanceDisplay selectedAssetId={selectedAssetId} apiEndpoint={`${API_BASE_URL}/analytics/predictive`} enableWebSocket={true} enableNotifications={true} assetIndex={assetById} /* --- UPDATE (v3): codes + constituency on rows */ />
                                        </Suspense>
                                    </SectionCard>
                                </AssetIntelligenceTab>
                            </Suspense>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
                {/* --- END UPDATE */}

            </VStack>

            {/* MODALS */}
            {editingUtility && (
                <Modal isOpen={isEditUtilModalOpen} onClose={onEditUtilModalClose} size="lg" isCentered>
                    <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                    <ModalContent bg={modalBodyBg} borderRadius="2xl" overflow="hidden">
                        <Box h="3px" bgGradient={headingGradient} />
                        <form onSubmit={handleSaveUtilityChanges}>
                            <ModalHeader>
                                <HStack spacing={3}>
                                    <Box p={2} bgGradient={headingGradient} borderRadius="lg"><Icon as={MdWaterDrop} boxSize={4} color="white" /></Box>
                                    <VStack align="start" spacing={0}><Text fontSize="lg" fontWeight="bold">Edit Utility Status</Text><Text fontSize="xs" color={grayColor}>{editingUtility.name}</Text></VStack>
                                </HStack>
                            </ModalHeader>
                            <ModalCloseButton borderRadius="full" />
                            <ModalBody pb={6}>
                                <VStack spacing={4}>
                                    <FormControl><FormLabel fontSize="sm" color={formLabelColor}>Utility Name (read-only)</FormLabel><Input value={editingUtility.name || ''} isReadOnly bg={readOnlyInputBg} borderRadius="lg" /></FormControl>
                                    <FormControl isRequired><FormLabel fontSize="sm" color={formLabelColor}>Status</FormLabel><Select name="status" value={editingUtility.status || ''} onChange={onChangeEdit} borderRadius="lg" focusBorderColor="teal.400">{ALLOWED_UTILITY_STATUS.map(stat => <option key={stat} value={stat}>{formatUtilityStatusLabel(stat)}</option>)}</Select></FormControl>
                                    <FormControl><FormLabel fontSize="sm" color={formLabelColor}>Description</FormLabel><Textarea name="description" value={editingUtility.description || ''} onChange={onChangeEdit} borderRadius="lg" focusBorderColor="teal.400" rows={3} /></FormControl>
                                    <FormControl><FormLabel fontSize="sm" color={formLabelColor}>Contact Info</FormLabel><Input name="contact_info" value={editingUtility.contact_info || ''} onChange={onChangeEdit} borderRadius="lg" focusBorderColor="teal.400" /></FormControl>
                                </VStack>
                            </ModalBody>
                            <ModalFooter borderTopWidth="1px" borderColor={borderColor} gap={3}>
                                <Button variant="ghost" onClick={onEditUtilModalClose} borderRadius="full">Cancel</Button>
                                <Button type="submit" colorScheme="teal" isLoading={isSubmitting} borderRadius="full" shadow="sm">Save Changes</Button>
                            </ModalFooter>
                        </form>
                    </ModalContent>
                </Modal>
            )}

            <AssetFormModal isOpen={isAssetModalOpen} onClose={onAssetModalClose} onSave={handleAssetFormSubmit} asset={editingAsset} />

            {failureReportAsset && (
                <ReportFailureModal isOpen={isFailureModalOpen} onClose={onFailureModalClose} asset={failureReportAsset}
                    // --- UPDATE H
                    onSubmit={handleSubmitFailureReport}
                    managerName={failureReportAsset.managed_by_org_id != null ? (intel.managers.find(m => String(m.id) === String(failureReportAsset.managed_by_org_id))?.name || null) : null} /* --- UPDATE (v3) */
                    defaultReporter={[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || currentUser?.username || ''}
                    assetId={failureReportAsset.id}
                    assetName={failureReportAsset.name || failureReportAsset.type}
                    insights={aiInsights.getInsightsForAsset(failureReportAsset.id)}
                    sensors={sensorsByAsset.get(failureReportAsset.id) || []}
                    // --- END UPDATE
                    onSuccess={() => { pubUtilCache.invalidate('mainData'); fetchData(); }}
                />
            )}

            {/* --- UPDATE (Public Utilities v2): Asset 360 drawer */}
            {drawer.asset && (
                <Suspense fallback={null}>
                    <AssetLifecycleDrawer
                        asset={drawer.asset}
                        isOpen={!!drawer.asset}
                        onClose={closeAsset360}
                        initialTab={drawer.tab}
                        scheduleId={drawer.scheduleId}
                        intel={intel}
                        canEdit={canEditAssets || ['inspector', 'manager', 'supervisor', 'contractor'].includes(currentUser?.role)}
                        canAdmin={canAdminPu}
                        onEditAsset={canEditAssets ? (a) => { closeAsset360(); handleEditAsset(assetById.get(a.id) || a); } : undefined}
                        onReportFailure={(a) => handleReportFailure(assetById.get(a.id) || a)}
                        onChanged={afterLifecycleChange}
                    />
                </Suspense>
            )}
            {/* --- END UPDATE */}
        </Box>
    );
}

export default PublicUtilitiesPage;